import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    Breadcrumbs,
    Link,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    RadioGroup,
    Radio,
    Autocomplete,
    Chip,
    IconButton,
    Card,
    CardContent,
    InputAdornment
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Print as PrintIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Calculate as CalculateIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    LocationCity as LocationCityIcon,
    LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { bookingService } from '../../../services/bookingService';
import { pincodeService } from '../../../services/pincodeService';
import franchiseService from '../../../services/franchiseService';
import branchService from '../../../services/branchService';
import DashboardLayout from '../../../components/layouts/DashboardLayout';
import debounce from 'lodash/debounce';


// Matching cities for the From/To pickers. Only the first matches are
// rendered: drawing all ~600 options on every open made the picker lag, and
// typing a few letters narrows the list to the wanted city anyway.
const CITY_OPTION_LIMIT = 100;
const filterCityOptions = (options, { inputValue }) => {
    const searchTerm = inputValue.toLowerCase();
    const matches = [];
    for (const option of options) {
        const cityName = option?.city_name || '';
        const cityShort = option?.city_short || '';
        if (cityName.toLowerCase().includes(searchTerm) || cityShort.toLowerCase().includes(searchTerm)) {
            matches.push(option);
            if (matches.length === CITY_OPTION_LIMIT) break;
        }
    }
    return matches;
};

const BookingFormPage = () => {
    const navigate = useNavigate();
    const { bookingId } = useParams();
    const location = useLocation();
    const { showSuccess, showError, showWarning, showInfo } = useApiHandler();
    
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    // Helper function to get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
        const now = new Date();
        return now.toISOString().split('T')[0];
    };

    // Helper function to get current time in HH:MM format
    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('en-IN', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Kolkata'
        });
    };

    // Convert time format for display (HTML5 time input uses 24-hour format)
    const formatTimeForDisplay = (time24h) => {
        if (!time24h) return '';
        
        // If it's already in the correct format for HTML5 time input, return as is
        if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time24h)) {
            return time24h;
        }
        
        // If it's in 12-hour format, convert to 24-hour
        if (time24h.includes('AM') || time24h.includes('PM')) {
            const [time, modifier] = time24h.split(' ');
            let [hours, minutes] = time.split(':');
            
            hours = parseInt(hours);
            
            if (modifier === 'PM' && hours !== 12) {
                hours += 12;
            } else if (modifier === 'AM' && hours === 12) {
                hours = 0;
            }
            
            return `${hours.toString().padStart(2, '0')}:${minutes}`;
        }
        
        return time24h;
    };

    const [formData, setFormData] = useState(() => {
        // Initialize form data with default values
        return {
            // General Details
            awb_no: '',
            booking_date: getCurrentDate(),
            booking_time: getCurrentTime(),
            TXT_B_OFF_ID: 0,
            TXT_ORI_ID: '',
            TXT_DES_ID: '',
            transport: 'Surface',
            booking_type: 'credit',
            payment_mode: 'BILL TO SHIPPER',
            instant_payment_type: 'to_pay',
            sa_no: '',
            dec_value: 0,

            // Shipper Details
            frm_name: '',
            frm_company: '',
            frm_address: '',
            frm_city: '',
            frm_state: '',
            frm_pincode: '',
            frm_tel: '',
            shipper_gst_no: '',

            // Recipient Details
            to_name: '',
            to_company: '',
            to_address: '',
            to_city: '',
            to_state: '',
            to_pincode: '',
            to_tel: '',
            recipient_gst_no: '',

            // Billing Details
            no_of_pack: 1,
            package_name: '',
            bill_by: 'Weight',
            product_id: '',
            product_tax_calculation_type: 'inclusive', // Store the selected product's tax calculation type
            act_wt: '',
            wt_l: '',
            wt_w: '',
            wt_h: '',
            charged_wt: '',

            // Billing customer (for rate calculation and invoice purposes)
            billing_customer_id: null,
            billing_customer_name: '',

            // Pricing
            actual_basic_freight: 0,
            actual_fuel_surcharge: 0,
            basic_freight: 0,
            fuel_surcharge: 0,
            discounted_basic_freight: 0,
            discounted_fuel_surcharge: 0,
            backend_discounted_freight: 0,
            backend_discounted_fuel: 0,
            handling_charges: 0,
            stat_charges: 0,
            to_pay_surcharge: 0,
            cod_charges: 0,
            sps_charges: 0,
            value_surc: 0,
            cr_charges: 0,
            remote_area_charges: 0,
            ser_tax: 0,
            total_amt: 0,
            grand_total: 0
        };
    });

    const [boxes, setBoxes] = useState([{ length: '', width: '', height: '', volumetric_weight: 0 }]);

    // State to track if form has been initially populated
    const [isFormPopulated, setIsFormPopulated] = useState(false);

    // State for package description
    const [showCustomPackageDesc, setShowCustomPackageDesc] = useState(false);
    const packageDescriptionOptions = [
        'Computer parts',
        'Footwear material',
        'Auto parts',
        'Medical items',
        'Others'
    ];

    // Additional state declarations
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isLoadingBooking, setIsLoadingBooking] = useState(false);
    
    // AWB labels are opened directly in new tab for new bookings
    
    // Product cache to avoid unnecessary API calls
    const [productCache, setProductCache] = useState(new Map());
    const [errorCache, setErrorCache] = useState(new Map()); // Cache for failed routes
    const [currentRoute, setCurrentRoute] = useState(null);
    
    // Form-specific states from BookingForm
    const [calculating, setCalculating] = useState(false);
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [bookingOffices, setBookingOffices] = useState([]);
    const [loadingBookingOffices, setLoadingBookingOffices] = useState(false);
    const [errors, setErrors] = useState({});
    const [baseRateInfo, setBaseRateInfo] = useState(null);

    // Selected billing customer and product states
    const [selectedBillingCustomer, setSelectedBillingCustomer] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Track if user manually edited freight fields (to determine which value to use in calculations)
    const [isBasicFreightEdited, setIsBasicFreightEdited] = useState(false);
    const [isFuelSurchargeEdited, setIsFuelSurchargeEdited] = useState(false);
    const [isDiscountedFreightEdited, setIsDiscountedFreightEdited] = useState(false);
    const [isDiscountedFuelEdited, setIsDiscountedFuelEdited] = useState(false);

    // Autocomplete states - Separate for shipper and recipient
    const [shipperSearchText, setShipperSearchText] = useState('');
    const [shipperOptions, setShipperOptions] = useState([]);
    const [shipperLoading, setShipperLoading] = useState(false);
    
    const [recipientSearchText, setRecipientSearchText] = useState('');
    const [recipientOptions, setRecipientOptions] = useState([]);
    const [recipientLoading, setRecipientLoading] = useState(false);
    
    // Address search states - Separate for shipper and recipient
    const [shipperAddressSearchText, setShipperAddressSearchText] = useState('');
    const [shipperAddressOptions, setShipperAddressOptions] = useState([]);
    const [shipperAddressLoading, setShipperAddressLoading] = useState(false);
    
    const [recipientAddressSearchText, setRecipientAddressSearchText] = useState('');
    const [recipientAddressOptions, setRecipientAddressOptions] = useState([]);
    const [recipientAddressLoading, setRecipientAddressLoading] = useState(false);
    
    // Third party customer states
    const [thirdPartyOptions, setThirdPartyOptions] = useState([]);
    const [thirdPartyLoading, setThirdPartyLoading] = useState(false);
    const [selectedThirdPartyCustomer, setSelectedThirdPartyCustomer] = useState(null);
    const [thirdPartySearchText, setThirdPartySearchText] = useState('');
    

    
    // Customer ID tracking for billing
    const [shipperCustomerId, setShipperCustomerId] = useState(null);
    const [recipientCustomerId, setRecipientCustomerId] = useState(null);
    const [thirdPartyCustomerId, setThirdPartyCustomerId] = useState(null);
    
    // Pincode states
    const [shipperPincodeInfo, setShipperPincodeInfo] = useState(null);
    const [recipientPincodeInfo, setRecipientPincodeInfo] = useState(null);
    const [pincodeLoading, setPincodeLoading] = useState({ shipper: false, recipient: false });

    // ===== MODE DETERMINATIONS (moved to top to avoid hoisting issues) =====
    // Determine if this is create, edit, or view mode
    const isCreateMode = !bookingId;
    const isEditMode = bookingId && !viewMode;
    const isViewMode = bookingId && viewMode;

    // ===== FUNCTION DEFINITIONS (moved to top to avoid hoisting issues) =====

    // Validate and determine tax calculation type
    const validateAndDetermineTaxType = useCallback((bookingData, selectedProduct, products) => {
        // Priority 1: From loaded booking data (most reliable for existing bookings)
        if (bookingData?.billing?.product?.tax_calculation_type) {
            const taxType = bookingData.billing.product.tax_calculation_type;
            if (['inclusive', 'exclusive'].includes(taxType)) {
                return { type: taxType, source: 'booking_data', isValid: true };
            }
        }
        
        // Priority 2: From selected product in form
        if (selectedProduct?.tax_calculation_type) {
            const taxType = selectedProduct.tax_calculation_type;
            if (['inclusive', 'exclusive'].includes(taxType)) {
                return { type: taxType, source: 'selected_product', isValid: true };
            }
        }
        
        // Priority 3: From products list by product ID
        if (bookingData?.billing?.product?.id && products.length > 0) {
            const productFromList = products.find(p => p.id === bookingData.billing.product.id);
            if (productFromList?.tax_calculation_type) {
                const taxType = productFromList.tax_calculation_type;
                if (['inclusive', 'exclusive'].includes(taxType)) {
                    return { type: taxType, source: 'products_list', isValid: true };
                }
            }
        }
        
        // If no valid tax type found, return error state
        return { 
            type: 'exclusive', // Default fallback
            source: 'fallback', 
            isValid: false,
            error: 'Product tax calculation type not found. Using default "inclusive" type.'
        };
    }, []);

    // Validate tax calculation consistency
    const validateTaxCalculationConsistency = useCallback((formData) => {
        const errors = [];
        
        // Check if tax type is valid
        if (!['inclusive', 'exclusive'].includes(formData.product_tax_calculation_type)) {
            errors.push('Invalid tax calculation type. Must be "inclusive" or "exclusive".');
        }
        
        // Check if we have required pricing data for validation
        if (formData.grand_total && formData.ser_tax && formData.total_amt) {
            const taxRate = 18; // 18% GST
            
            if (formData.product_tax_calculation_type === 'inclusive') {
                // For inclusive: grand_total should equal total_amt + ser_tax
                const expectedGrandTotal = formData.total_amt + formData.ser_tax;
                const tolerance = 0.01; // 1 paisa tolerance
                
                if (Math.abs(formData.grand_total - expectedGrandTotal) > tolerance) {
                    errors.push('Tax calculation inconsistency detected for inclusive tax type.');
                }
            } else if (formData.product_tax_calculation_type === 'exclusive') {
                // For exclusive: ser_tax should be 18% of total_amt
                const expectedTax = formData.total_amt * (taxRate / 100);
                const tolerance = 0.01; // 1 paisa tolerance
                
                if (Math.abs(formData.ser_tax - expectedTax) > tolerance) {
                    errors.push('Tax calculation inconsistency detected for exclusive tax type.');
                }
            }
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }, []);

    // Populate form data from booking
    const populateFormData = useCallback((bookingData) => {
        
        // Format time for HTML5 time input (24-hour format)
        const displayTime = bookingData.booking_time ? formatTimeForDisplay(bookingData.booking_time) : '';
        
        const populated = {
            // General Details
            awb_no: bookingData.awb_no || '',
            booking_date: bookingData.booking_date || '',
            booking_time: displayTime,
            TXT_B_OFF_ID: bookingData.booking_office?.id ?? 0,
            TXT_ORI_ID: bookingData.from_city?.id || '',
            TXT_DES_ID: bookingData.to_city?.id || '',
            transport: bookingData.transport || 'Surface',
            booking_type: bookingData.booking_type || 'credit',
            payment_mode: bookingData.payment_mode || 'BILL TO SHIPPER',
            instant_payment_type: bookingData.instant_payment_type || 'to_pay',
            sa_no: bookingData.ref_no || '',
            dec_value: bookingData.declared_value || 0,

            // Shipper Details
            frm_name: bookingData.shipper?.name || '',
            frm_company: bookingData.shipper?.company || '',
            frm_address: bookingData.shipper?.address || '',
            frm_city: bookingData.shipper?.city || '',
            frm_state: bookingData.shipper?.state || '',
            frm_pincode: bookingData.shipper?.pincode || '',
            frm_tel: bookingData.shipper?.mobile || '',
            shipper_gst_no: bookingData.shipper?.gst_no || '',

            // Recipient Details
            to_name: bookingData.recipient?.name || '',
            to_company: bookingData.recipient?.company || '',
            to_address: bookingData.recipient?.address || '',
            to_city: bookingData.recipient?.city || '',
            to_state: bookingData.recipient?.state || '',
            to_pincode: bookingData.recipient?.pincode || '',
            to_tel: bookingData.recipient?.mobile || '',
            recipient_gst_no: bookingData.recipient?.gst_no || '',

            // Billing Details
            no_of_pack: bookingData.billing?.no_of_packs || 1,
            package_name: bookingData.billing?.package_name || '',
            bill_by: bookingData.billing?.bill_by || 'Weight',
            product_id: bookingData.billing?.product?.id || '',
            act_wt: bookingData.billing?.actual_weight || '',
            wt_l: bookingData.billing?.dimensions?.length || '',
            wt_w: bookingData.billing?.dimensions?.width || '',
            wt_h: bookingData.billing?.dimensions?.height || '',
            charged_wt: bookingData.billing?.charged_weight || '',

            // Billing customer (for rate calculation and invoice purposes)
            billing_customer_id: bookingData.billing?.customer_id || null,
            billing_customer_name: bookingData.billing?.customer_name || '',

            // Pricing - Convert stored values to display values based on tax type
            actual_basic_freight: bookingData.pricing?.actual_basic_freight || 0,
            actual_fuel_surcharge: bookingData.pricing?.actual_fuel_surcharge || 0,

            // Calculate display values based on product's tax type
            // For INCLUSIVE: show tax-reduced, for EXCLUSIVE: show full
            basic_freight: bookingData.pricing?.basic_freight || 0,
            fuel_surcharge: bookingData.pricing?.fuel_surcharge || 0,
            discounted_basic_freight: bookingData.pricing?.basic_freight || 0, // Will be recalculated below
            discounted_fuel_surcharge: bookingData.pricing?.fuel_surcharge || 0,

            // Backend calculation values (same as actual for edit mode)
            backend_discounted_freight: bookingData.pricing?.basic_freight || 0,
            backend_discounted_fuel: bookingData.pricing?.fuel_surcharge || 0,

            handling_charges: bookingData.pricing?.additional_charges?.handling || 0,
            stat_charges: bookingData.pricing?.additional_charges?.statistical || 0,
            to_pay_surcharge: bookingData.pricing?.additional_charges?.to_pay || 0,
            cod_charges: bookingData.pricing?.additional_charges?.cod_charges || 0,
            sps_charges: bookingData.pricing?.additional_charges?.special_service || 0,
            value_surc: bookingData.pricing?.additional_charges?.value_surcharge || 0,
            cr_charges: bookingData.pricing?.additional_charges?.carriers_risk || 0,
            remote_area_charges: bookingData.pricing?.additional_charges?.remote_area || 0,
            ser_tax: bookingData.pricing?.gst || 0,
            total_amt: bookingData.pricing?.total_amount || 0,
            grand_total: bookingData.pricing?.grand_total || 0,

            // Store product tax type with validation
            product_tax_calculation_type: (() => {
                const taxValidation = validateAndDetermineTaxType(bookingData, null, []);
                if (!taxValidation.isValid) {
                    console.warn('Tax type validation failed:', taxValidation.error);
                    // Show warning to user in edit mode
                    if (isEditMode) {
                        showWarning(taxValidation.error);
                    }
                }
                return taxValidation.type;
            })()
        };

        // Update form data with populated values
        setFormData(populated);

        // Load boxes for editing
        const apiBoxes = bookingData?.billing?.boxes;
        const dims = bookingData?.billing?.dimensions;
        if (Array.isArray(apiBoxes) && apiBoxes.length > 0) {
            // Multi-box booking: load all boxes
            setBoxes(apiBoxes.map(box => ({
                length: box.length.toString(),
                width: box.width.toString(),
                height: box.height.toString(),
                volumetric_weight: box.volumetric_weight,
            })));
        } else if (dims && (dims.length > 0 || dims.width > 0 || dims.height > 0)) {
            // Legacy single-dimension booking: load as single box
            setBoxes([{
                length: dims.length.toString(),
                width: dims.width.toString(),
                height: dims.height.toString(),
                volumetric_weight: 0,
            }]);
        }

        // Check if package description matches predefined options
        const packageName = bookingData.billing?.package_name || '';
        if (packageName && !packageDescriptionOptions.includes(packageName)) {
            setShowCustomPackageDesc(true);
        }

        // Set customer IDs based on payment mode and billing customer
        if (populated.billing_customer_id) {
            
            setSelectedBillingCustomer(populated.billing_customer_id);
            
            // Set the appropriate customer ID based on payment mode
            if (populated.payment_mode === 'BILL TO SHIPPER') {
                setShipperCustomerId(populated.billing_customer_id);
            } else if (populated.payment_mode === 'BILL TO RECEIPIENT') {
                setRecipientCustomerId(populated.billing_customer_id);
            } else if (populated.payment_mode === 'BILL TO THIRD PARTY') {
                setThirdPartyCustomerId(populated.billing_customer_id);
                setSelectedThirdPartyCustomer({
                    id: populated.billing_customer_id,
                    cust_name: populated.billing_customer_name || populated.third_party_customer_name || ''
                });
            }
        }
        
        // Set product if available
        if (populated.product_id) {
            setSelectedProduct(populated.product_id);
        }
        
        // Set route for product loading
        if (bookingData.from_city?.id && bookingData.to_city?.id) {
            const routeKey = `${bookingData.from_city.id}-${bookingData.to_city.id}`;
            setCurrentRoute(routeKey);
        }
    }, [formatTimeForDisplay, setSelectedBillingCustomer, setSelectedProduct]);

    // Load cities
    const loadCities = useCallback(async () => {
        setLoadingCities(true);
        try {
            const response = await bookingService.getCities();
            if (response.success) {
                setCities(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch cities:', error);
            showError('Failed to fetch cities');
        } finally {
            setLoadingCities(false);
        }
    }, [showError]);

    // Load booking offices
    const loadBookingOffices = useCallback(async () => {
        setLoadingBookingOffices(true);
        try {
            const allOffices = [];

            // Load branches
            try {
                const branchResponse = await branchService.getActiveBranches();
                const branches = (branchResponse.data || []).map(branch => ({
                    id: branch.branch_id,
                    name: branch.branch_name,
                    type: 'branch',
                    city: 'Branch Office',
                    address: branch.branch_address || 'N/A'
                }));
                allOffices.push(...branches);
            } catch (error) {
                console.error('Failed to load branches:', error);
            }

            // Load franchises
            try {
                // /franchises is paginated (10 by default); the office picker needs all.
                const franchiseResponse = await franchiseService.getFranchises({ per_page: 100 });
                const franchises = (franchiseResponse.data || []).map(franchise => ({
                    id: franchise.id,
                    name: franchise.fname && franchise.lname ? `${franchise.fname} ${franchise.lname}` : franchise.franchise_name || 'Unnamed Franchise',
                    type: 'franchise',
                    city: franchise.city || 'N/A',
                    address: franchise.address || 'N/A',
                    original_id: franchise.id
                })).filter(franchise => franchise.id && franchise.name);
                allOffices.push(...franchises);
            } catch (error) {
                console.error('Failed to load franchises:', error);
            }

            setBookingOffices(allOffices);
        } catch (error) {
            console.error('Failed to load booking offices:', error);
            showError('Failed to load booking offices');
            setBookingOffices([]);
        } finally {
            setLoadingBookingOffices(false);
        }
    }, [showError]);

    // Generate AWB number
    const generateAwbNumber = useCallback(async () => {
        try {
            const response = await bookingService.generateAwbNumber();
            if (response.success) {
                setFormData(prev => {
                    // Only update if the AWB number is different to prevent flickering
                    if (prev.awb_no !== response.data) {
                        return { ...prev, awb_no: response.data };
                    }
                    return prev;
                });
            }
        } catch (error) {
            console.error('Failed to generate AWB number:', error);
            showError('Failed to generate AWB number');
        }
    }, [showError]);

    // Handle pincode change
    const handlePincodeChange = useCallback(async (pincode, isRecipient = false, isManualEntry = true) => {
        const prefix = isRecipient ? 'to_' : 'frm_';
        
        // Always update the pincode field value first
        setFormData(prev => ({
            ...prev,
            [`${prefix}pincode`]: pincode
        }));
        
        // Clear any existing errors when user is typing
        if (pincode.length < 6) {
            setErrors(prev => ({
                ...prev,
                [`${prefix}pincode`]: null
            }));
            return;
        }
        
        // Only validate and fetch details if pincode is exactly 6 digits
        if (!pincode || pincode.length !== 6) return;
        
        // Only call public pincode API when user typed manually
        const shouldFetchFromAPI = isManualEntry === true;
        
        if (shouldFetchFromAPI) {
            try {
                const response = await pincodeService.getPincodeDetails(pincode);
                if (response.success && response.data) {
                    const locationData = response.data;
                    
                    setFormData(prev => ({
                        ...prev,
                        [`${prefix}city`]: locationData.city || '',
                        [`${prefix}state`]: locationData.state || ''
                    }));
                    
                    // Clear any existing errors for this pincode
                    setErrors(prev => ({
                        ...prev,
                        [`${prefix}pincode`]: null
                    }));
                } else {
                    // Set error if pincode not found
                    setErrors(prev => ({
                        ...prev,
                        [`${prefix}pincode`]: 'Invalid pincode'
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch pincode details:', error);
                setErrors(prev => ({
                    ...prev,
                    [`${prefix}pincode`]: 'Failed to validate pincode'
                }));
            }
        } else {
            // Skip API call when populated via master data selection; do not override fields here
            return;
        }
    }, [booking]);

    // Route whose products were asked for last, so a slow response for an
    // earlier route never replaces the list for the current one
    const requestedRouteKeyRef = useRef(null);

    // Optimized product loading with caching - Define early to avoid hoisting issues
    const loadProducts = useCallback(async (fromCityId, toCityId, customerId = null) => {
        if (!fromCityId || !toCityId) {
            requestedRouteKeyRef.current = null;
            setProducts([]);
            setCurrentRoute(null);
            return;
        }

        // The customer is part of the key: the offer is narrowed to the packages
        // assigned to them, so caching by route alone would serve one customer's
        // package list to the next.
        const routeKey = `${fromCityId}-${toCityId}-${customerId || 'any'}`;
        requestedRouteKeyRef.current = routeKey;
        
        // Check if we already have products for this route in cache
        if (productCache.has(routeKey)) {
            const cachedProducts = productCache.get(routeKey);
            setProducts(cachedProducts);
            setCurrentRoute(routeKey);
            return;
        }

        // Check if this route previously failed (to avoid repeated API calls)
        if (errorCache.has(routeKey)) {
            setProducts([]);
            setCurrentRoute(routeKey);
            return;
        }

        // Check if we're already loading products for this route
        if (loadingProducts && currentRoute === routeKey) {
            return;
        }

        setLoadingProducts(true);
        setCurrentRoute(routeKey);
        
        try {
            const response = await bookingService.getProductsForRoute(fromCityId, toCityId, customerId);
            
            if (response.success) {
                const productsData = response.data.products || [];
                
                // Cache the products for this route with size limit
                setProductCache(prev => {
                    const newCache = new Map(prev);
                    
                    // Limit cache size to 20 routes to prevent memory issues
                    if (newCache.size >= 20) {
                        // Remove the oldest entry (first key)
                        const firstKey = newCache.keys().next().value;
                        newCache.delete(firstKey);
                    }
                    
                    newCache.set(routeKey, productsData);
                    return newCache;
                });

                // Show them now rather than on the next pass through the cache
                if (requestedRouteKeyRef.current === routeKey) {
                    setProducts(productsData);
                }
                
                // Remove from error cache if it was there
                setErrorCache(prev => {
                    const newErrorCache = new Map(prev);
                    newErrorCache.delete(routeKey);
                    return newErrorCache;
                });
                
                if (productsData.length === 0) {
                    showWarning('No products/services available for this route. Please select a different route or contact support.');
                }
            } else {
                setProducts([]);
                // Cache the error to prevent repeated API calls
                setErrorCache(prev => {
                    const newErrorCache = new Map(prev);
                    newErrorCache.set(routeKey, response.message || 'Failed to load products for selected route');
                    return newErrorCache;
                });
                showError(response.message || 'Failed to load products for selected route');
            }
        } catch (error) {
            console.error(`Network error for route ${routeKey}:`, error);
            setProducts([]);
            // Cache the error to prevent repeated API calls
            setErrorCache(prev => {
                const newErrorCache = new Map(prev);
                newErrorCache.set(routeKey, 'Unable to load products for this route. Please try again.');
                return newErrorCache;
            });
            showError('Unable to load products for this route. Please try again.');
        } finally {
            setLoadingProducts(false);
        }
    }, [productCache, errorCache, loadingProducts, currentRoute, showError, showWarning]);





    // Helper function to safely format discount percentages
    const formatDiscountPercent = (value) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? 0 : numValue.toFixed(1);
    };

    // Memoized totals calculation
    const calculatedTotals = useMemo(() => {
        // Get current values from state and ensure they are numbers
        const getCurrentValue = (field) => {
            const value = formData[field];
            return parseFloat(value) || 0;
        };

        const additionalCharges = [
            getCurrentValue('handling_charges'),
            getCurrentValue('stat_charges'),
            getCurrentValue('to_pay_surcharge'),
            getCurrentValue('cod_charges'),
            getCurrentValue('sps_charges'),
            getCurrentValue('value_surc'),
            getCurrentValue('cr_charges'),
            getCurrentValue('remote_area_charges')
        ].reduce((sum, charge) => sum + charge, 0);

        const taxRate = 18; // 18% GST

        // Determine which freight values to use:
        // - If user EDITED the field → use the edited value from basic_freight
        // - If user NOT edited → use actual_basic_freight (original from backend)
        let freightToUse, fuelToUse;

        if (isBasicFreightEdited) {
            // User manually edited → use the displayed/edited value
            freightToUse = getCurrentValue('basic_freight');
        } else {
            // User did NOT edit → use actual value from backend
            freightToUse = getCurrentValue('actual_basic_freight');
        }

        if (isFuelSurchargeEdited) {
            // User manually edited → use the displayed/edited value
            fuelToUse = getCurrentValue('fuel_surcharge');
        } else {
            // User did NOT edit → use actual value from backend
            fuelToUse = getCurrentValue('actual_fuel_surcharge');
        }

        let baseTotal, taxAmount, grandTotal;

        if (formData.product_tax_calculation_type === 'inclusive') {
            // INCLUSIVE TAX LOGIC:
            // Total amount INCLUDES tax, so we need to extract the tax
            const totalWithTax = freightToUse + fuelToUse + additionalCharges;

            // Extract base from inclusive amount: base = total / 1.18
            baseTotal = totalWithTax / (1 + (taxRate / 100));

            // Tax is the difference
            taxAmount = totalWithTax - baseTotal;

            // Grand total IS the total (tax already included)
            grandTotal = totalWithTax;

        } else {
            // EXCLUSIVE TAX LOGIC:
            // Tax is added on top of the base amount
            baseTotal = freightToUse + fuelToUse + additionalCharges;

            // Calculate tax on the base
            taxAmount = baseTotal * (taxRate / 100);

            // Grand total = base + tax
            grandTotal = baseTotal + taxAmount;
        }

        return {
            total_amt: bookingService.roundToTwoDecimals(baseTotal),
            ser_tax: bookingService.roundToTwoDecimals(taxAmount),
            grand_total: bookingService.roundToTwoDecimals(grandTotal)
        };
    }, [
        formData.handling_charges,
        formData.stat_charges,
        formData.to_pay_surcharge,
        formData.cod_charges,
        formData.sps_charges,
        formData.value_surc,
        formData.cr_charges,
        formData.remote_area_charges,
        formData.basic_freight,
        formData.actual_basic_freight,
        formData.fuel_surcharge,
        formData.actual_fuel_surcharge,
        formData.product_tax_calculation_type,
        isBasicFreightEdited,
        isFuelSurchargeEdited
    ]);

    // Calculate totals function
    const calculateTotals = useCallback(() => {
        setFormData(prev => {
            // Check if values actually changed
            const hasChanged = prev.total_amt !== calculatedTotals.total_amt || 
                             prev.ser_tax !== calculatedTotals.ser_tax || 
                             prev.grand_total !== calculatedTotals.grand_total;
            
            if (hasChanged) {
                return { ...prev, ...calculatedTotals };
            }
            return prev;
        });
    }, [calculatedTotals]);

    // Auto-calculate totals when pricing fields change
    useEffect(() => {
        calculateTotals();
    }, [
        formData.handling_charges,
        formData.stat_charges,
        formData.to_pay_surcharge,
        formData.cod_charges,
        formData.sps_charges,
        formData.value_surc,
        formData.cr_charges,
        formData.remote_area_charges,
        formData.actual_basic_freight,
        formData.actual_fuel_surcharge,
        formData.basic_freight,
        formData.fuel_surcharge,
        formData.product_tax_calculation_type
    ]);

    // Validate tax calculation consistency when form data changes
    useEffect(() => {
        if (isFormPopulated && formData.product_tax_calculation_type) {
            const validation = validateTaxCalculationConsistency(formData);
            if (!validation.isValid) {
                console.warn('Tax calculation validation errors:', validation.errors);
                // Show errors to user in edit mode
                if (isEditMode) {
                    validation.errors.forEach(error => {
                        showError(error);
                    });
                }
            }
        }
    }, [
        formData.product_tax_calculation_type,
        formData.grand_total,
        formData.ser_tax,
        formData.total_amt,
        isFormPopulated,
        isEditMode,
        validateTaxCalculationConsistency
    ]);

    // Handle manual recalculation for inclusive tax products
    // This will only run when user manually changes values, not on initial load
    const handleManualRecalculation = useCallback(() => {
        if (formData.product_tax_calculation_type === 'inclusive') {
            
            const taxRate = 18;
            const originalInclusiveAmount = formData.basic_freight || formData.actual_basic_freight;
            const originalFuelSurcharge = formData.fuel_surcharge || formData.actual_fuel_surcharge;
            
            // Only recalculate if we have valid amounts and discounts
            if (originalInclusiveAmount > 0 && (formData.package_discount > 0 || formData.fuel_discount > 0)) {
                
                // Step 1: Split inclusive amount into base and tax
                const basicFreightBase = originalInclusiveAmount / (1 + (taxRate / 100));
                const fuelSurchargeBase = originalFuelSurcharge / (1 + (taxRate / 100));
                
                // Step 2: Apply discount to base amounts
                const packageDiscountDecimal = (formData.package_discount || 0) / 100;
                const fuelDiscountDecimal = (formData.fuel_discount || 0) / 100;
                
                const discountedBasicFreightBase = basicFreightBase * (1 - packageDiscountDecimal);
                const discountedFuelSurchargeBase = fuelSurchargeBase * (1 - fuelDiscountDecimal);
                
                // Update display values
                setFormData(prev => ({
                    ...prev,
                    basic_freight: bookingService.roundToTwoDecimals(basicFreightBase),
                    fuel_surcharge: bookingService.roundToTwoDecimals(fuelSurchargeBase),
                    actual_basic_freight: bookingService.roundToTwoDecimals(discountedBasicFreightBase),
                    actual_fuel_surcharge: bookingService.roundToTwoDecimals(discountedFuelSurchargeBase)
                }));
            }
        }
    }, [formData.product_tax_calculation_type, formData.package_discount, formData.fuel_discount, formData.basic_freight, formData.fuel_surcharge]);

    // Auto-update billing customer ID when customer IDs change (only for new bookings)
    useEffect(() => {
        // Don't auto-update billing customer ID in edit mode to preserve existing data
        if (isEditMode) {
            return;
        }

        // Skip for instant bookings - they don't have billing customers
        if (formData.booking_type === 'instant') {
            if (formData.billing_customer_id !== null) {
                setFormData(prev => ({
                    ...prev,
                    billing_customer_id: null,
                    billing_customer_name: ''
                }));
            }
            return;
        }

        const newBillingCustomerId = determineBillingCustomerId();
        if (newBillingCustomerId !== formData.billing_customer_id) {
            setFormData(prev => ({
                ...prev,
                billing_customer_id: newBillingCustomerId,
                billing_customer_name: newBillingCustomerId ?
                    (prev.payment_mode === 'BILL TO SHIPPER' ? prev.frm_name :
                     prev.payment_mode === 'BILL TO RECEIPIENT' ? prev.to_name :
                     prev.third_party_customer_name) : ''
            }));
        }
    }, [shipperCustomerId, recipientCustomerId, thirdPartyCustomerId, formData.payment_mode, formData.billing_customer_id, formData.frm_name, formData.to_name, formData.third_party_customer_name, formData.booking_type, isEditMode]);

    // Determine billing customer ID based on booking type and payment mode
    const determineBillingCustomerId = () => {
        // For instant bookings, no billing customer is needed
        if (formData.booking_type === 'instant') {
            return null;
        }

        let billingCustomerId = null;

        if (formData.payment_mode === 'BILL TO SHIPPER') {
            billingCustomerId = shipperCustomerId;
        } else if (formData.payment_mode === 'BILL TO RECEIPIENT') {
            billingCustomerId = recipientCustomerId;
        } else if (formData.payment_mode === 'BILL TO THIRD PARTY') {
            billingCustomerId = thirdPartyCustomerId;
        }

        // Fallback: If individual customer IDs are not set (can happen in edit mode),
        // use the billing_customer_id from form data
        if (!billingCustomerId && formData.billing_customer_id) {
            billingCustomerId = formData.billing_customer_id;
        }
        return billingCustomerId;
    };

    // Robust form data update function to prevent race conditions
    const updateFormData = useCallback((updates) => {
        setFormData(prevData => {
            // If updates is a function, call it with previous data
            if (typeof updates === 'function') {
                return updates(prevData);
            }
            
            // Check if there are actual changes to prevent unnecessary updates
            const hasChanges = Object.keys(updates).some(key => 
                prevData[key] !== updates[key]
            );
            
            if (!hasChanges) {
                return prevData; // No changes, return same object to prevent re-renders
            }
            
            // If updates is an object, merge it with previous data
            const newData = { ...prevData, ...updates };
            
            return newData;
        });
    }, []);

    useEffect(() => {
        // Check if view mode is requested via query params
        const urlParams = new URLSearchParams(location.search);
        const shouldView = urlParams.get('view') === 'true';
        setViewMode(shouldView);
    }, [location.search]);

    // Reset form populated flag when booking ID changes
    useEffect(() => {
        setIsFormPopulated(false);
    }, [bookingId]);

    // Load booking data
    useEffect(() => {
        if (bookingId) {
            // Define loadBooking inline to avoid hoisting issues
            const loadBookingData = async () => {
                setLoading(true);
                setIsLoadingBooking(true);
                try {
                    const response = await bookingService.getBookingById(bookingId);
                    if (response.success) {
                        const bookingData = response.data;
                        setBooking(bookingData);
                        
                        // Use the comprehensive populateFormData function to populate all fields
                        // Only populate if not already populated to prevent overwriting user changes
                        if (!isFormPopulated) {
                            populateFormData(bookingData);
                            setIsFormPopulated(true);
                        }
                        
                        // Load products for the existing route if both cities are available
                        if (bookingData.from_city?.id && bookingData.to_city?.id) {
                            // Keep the key shape in step with currentRouteKey, which
                            // includes the billing customer.
                            const editBillingId = bookingData.billing?.customer_id || null;
                            const routeKey = `${bookingData.from_city.id}-${bookingData.to_city.id}-${editBillingId || 'any'}`;
                            setCurrentRoute(routeKey);

                            // Use setTimeout to ensure this happens after the initial state updates
                            const timeoutId = setTimeout(() => {
                                loadProducts(bookingData.from_city.id, bookingData.to_city.id, editBillingId).then(() => {
                                    // Re-validate tax type after products are loaded
                                    if (bookingData.billing?.product?.id) {
                                        const taxValidation = validateAndDetermineTaxType(bookingData, null, products);
                                        if (!taxValidation.isValid) {
                                            console.warn('Tax type re-validation failed after products loaded:', taxValidation.error);
                                            showWarning(taxValidation.error);
                                        }
                                    }
                                });
                            }, 100);
                            
                            // Cleanup timeout on component unmount
                            return () => clearTimeout(timeoutId);
                        }
                    } else {
                        showError(response.message || 'Failed to load booking');
                        navigate('/employee/bookings');
                    }
                } catch (error) {
                    console.error('Error loading booking:', error);
                    showError('Failed to load booking details');
                    navigate('/employee/bookings');
                } finally {
                    setLoading(false);
                    // Delay clearing the loading booking flag to prevent premature product clearing
                    const timeoutId = setTimeout(() => {
                        setIsLoadingBooking(false);
                    }, 500);
                }
            };
            
            loadBookingData();
        }
    }, [bookingId, navigate, showError]);

    // Load initial data
    useEffect(() => {
        loadCities();
        loadBookingOffices();
        
        if (!bookingId) {
            // For new bookings, set default values
            generateAwbNumber();
            // Set current time for new bookings only
            const currentTime = getCurrentTime();
            setFormData(prev => ({
                ...prev,
                booking_time: currentTime
            }));
        }
    }, [bookingId, loadCities, loadBookingOffices, generateAwbNumber]);

    // Debounced city change handler to prevent excessive rerenders
    const debouncedCityChange = useCallback(
        debounce((field, value) => {
            // Only trigger rate calculation and product loading after a delay
            if (['TXT_ORI_ID', 'TXT_DES_ID'].includes(field)) {
                // This will be handled by the existing useEffect hooks with debouncing
            }
        }, 500),
        []
    );

    // Form input change handler
    const handleInputChange = (field, value) => {
        // Track manual edits to freight fields
        if (field === 'basic_freight') {
            setIsBasicFreightEdited(true);
        }
        if (field === 'fuel_surcharge') {
            setIsFuelSurchargeEdited(true);
        }
        if (field === 'discounted_basic_freight') {
            setIsDiscountedFreightEdited(true);
        }
        if (field === 'discounted_fuel_surcharge') {
            setIsDiscountedFuelEdited(true);
        }

        // For numeric fields, ensure proper parsing
        let processedValue = value;
        const numericFields = ['handling_charges', 'stat_charges', 'to_pay_surcharge', 'cod_charges', 'sps_charges', 'value_surc', 'cr_charges', 'remote_area_charges', 'basic_freight', 'fuel_surcharge', 'actual_basic_freight', 'actual_fuel_surcharge', 'TXT_B_OFF_ID'];

        if (numericFields.includes(field)) {
            // Convert to number for ID fields, handle empty values properly
            if (['TXT_B_OFF_ID'].includes(field)) {
                processedValue = value === '' ? '' : parseInt(value) || 0;
            } else {
                // Validate decimal input - only allow up to 2 decimal places
                if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) {
                    return; // Don't update if invalid decimal format
                }
                // Convert to number and back to string to handle decimal inputs properly
                const numValue = parseFloat(value) || 0;
                processedValue = value === '' ? '' : numValue.toString();
            }
        }

        // Handle city ID fields separately to avoid conversion issues
        if (['TXT_ORI_ID', 'TXT_DES_ID'].includes(field)) {
            processedValue = value === '' ? '' : value;
            // Use debounced handler for city changes to prevent excessive rerenders
            debouncedCityChange(field, processedValue);
        }

        if (field === 'product_id') {
            processedValue = value === '' ? '' : Number(value);
        }

        setFormData(prev => ({ ...prev, [field]: processedValue }));
        
        // Clear related errors
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }

        // Real-time validation for package description
        if (field === 'package_name') {
            const trimmedValue = value.trim();
            let fieldError = null;
            
            if (!trimmedValue) {
                fieldError = 'Package description is required';
            } else if (trimmedValue.length < 3) {
                fieldError = 'Package description must be at least 3 characters long';
            } else if (trimmedValue.length > 200) {
                fieldError = 'Package description cannot exceed 200 characters';
            } else if (!/^[a-zA-Z0-9\s\-_.,()&]+$/.test(trimmedValue)) {
                fieldError = 'Package description can only contain letters, numbers, spaces, hyphens, underscores, commas, periods, parentheses, and ampersands';
            }
            
            setErrors(prev => ({ ...prev, [field]: fieldError }));
        }

        // Real-time validation for mobile numbers
        // if (field === 'frm_tel' || field === 'to_tel') {
        //     const mobileValue = value.trim();
        //     let fieldError = null;
            
        //     if (mobileValue && !/^[6-9]\d{9}$/.test(mobileValue)) {
        //         fieldError = 'Please enter a valid 10-digit mobile number';
        //     }
            
        //     setErrors(prev => ({ ...prev, [field]: fieldError }));
        // }

        // Real-time validation for pincode
        if (field === 'frm_pincode' || field === 'to_pincode') {
            const pincodeValue = value.trim();
            let fieldError = null;
            
            if (pincodeValue && !/^\d{6}$/.test(pincodeValue)) {
                fieldError = 'Please enter a valid 6-digit pincode';
            }
            
            setErrors(prev => ({ ...prev, [field]: fieldError }));
        }

        // Handle payment mode change
        if (field === 'payment_mode') {
            handlePaymentModeChange(value);
        }

        // Handle product selection
        if (field === 'product_id') {
            const selectedProduct = products.find(p => p.id === value);
            if (selectedProduct) {
                const taxValidation = validateAndDetermineTaxType(null, selectedProduct, products);
                if (!taxValidation.isValid) {
                    console.warn('Product tax type validation failed:', taxValidation.error);
                    showWarning(taxValidation.error);
                }
                
                setFormData(prev => ({ 
                    ...prev, 
                    product_tax_calculation_type: taxValidation.type
                }));
                
                // Update selected product state
                setSelectedProduct(selectedProduct);
            }
        }

        // Handle bill by change
        if (field === 'bill_by') {
            handleBillByChange(value);
        }

        // Handle weight changes: recalculate charged weight from boxes if billing by dimension
        if (field === 'act_wt' && formData.bill_by === 'Dimention' && boxes.length > 0) {
            const result = bookingService.calculateMultiBoxWeight(boxes, formData.transport);
            const totalVol = result.totalVolumetricWeight;
            const actWt = parseFloat(value) || 0;
            setFormData(prev => ({ ...prev, charged_wt: Math.max(totalVol, actWt) }));
        } else if (['act_wt', 'wt_l', 'wt_w', 'wt_h'].includes(field)) {
            calculateChargedWeight(field, value);
        }

        // Handle transport changes for dimension-based billing
        if (field === 'transport' && formData.bill_by === 'Dimention') {
            if (boxes.length > 0) {
                const result = bookingService.calculateMultiBoxWeight(boxes, value);
                const totalVol = result.totalVolumetricWeight;
                const actWt = parseFloat(formData.act_wt) || 0;

                setBoxes(result.boxes.map((b, i) => ({
                    length: boxes[i].length,
                    width: boxes[i].width,
                    height: boxes[i].height,
                    volumetric_weight: b.volumetric_weight,
                })));
                setFormData(prev => ({ ...prev, charged_wt: Math.max(totalVol, actWt) }));
            } else if (formData.wt_l && formData.wt_w && formData.wt_h) {
                const dimensionalWeight = bookingService.calculateDimensionalWeight(
                    formData.wt_l, formData.wt_w, formData.wt_h, value
                );
                setFormData(prev => ({ ...prev, charged_wt: dimensionalWeight }));
            }
        }
    };

    // Multi-box dimension handlers
    const handleBoxChange = (index, field, value) => {
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

        setBoxes(prev => {
            const updated = prev.map((box, i) => {
                if (i !== index) return box;
                return { ...box, [field]: value };
            });

            // Recalculate volumetric weights
            const result = bookingService.calculateMultiBoxWeight(updated, formData.transport);
            const updatedWithWeights = result.boxes;
            const totalVol = result.totalVolumetricWeight;
            const actWt = parseFloat(formData.act_wt) || 0;
            const chargedWt = Math.max(totalVol, actWt);

            setFormData(prev => ({
                ...prev,
                charged_wt: chargedWt,
                no_of_pack: updated.length,
            }));

            return updatedWithWeights.map((b, i) => ({
                length: updated[i].length,
                width: updated[i].width,
                height: updated[i].height,
                volumetric_weight: b.volumetric_weight,
            }));
        });
    };

    // Number of Packages drives how many box (L×W×H) rows are shown.
    // Growing keeps existing box values and appends empty boxes; shrinking trims from the end.
    const handleNumPackagesChange = (rawValue) => {
        let count = parseInt(rawValue, 10);
        if (isNaN(count) || count < 1) count = 1;

        // Weight billing: just update the count
        if (formData.bill_by !== 'Dimention') {
            setFormData(prev => ({ ...prev, no_of_pack: count }));
            setErrors(prev => ({ ...prev, no_of_pack: null }));
            return;
        }

        // Dimension billing: resize the box rows to match the package count
        let updated = [...boxes];
        if (count > updated.length) {
            while (updated.length < count) {
                updated.push({ length: '', width: '', height: '', volumetric_weight: 0 });
            }
        } else if (count < updated.length) {
            updated = updated.slice(0, count);
        }

        const result = bookingService.calculateMultiBoxWeight(updated, formData.transport);
        const totalVol = result.totalVolumetricWeight;
        const actWt = parseFloat(formData.act_wt) || 0;

        setBoxes(result.boxes.map((b, i) => ({
            length: updated[i].length,
            width: updated[i].width,
            height: updated[i].height,
            volumetric_weight: b.volumetric_weight,
        })));
        setFormData(prev => ({ ...prev, no_of_pack: count, charged_wt: Math.max(totalVol, actWt) }));
        setErrors(prev => ({ ...prev, no_of_pack: null }));
    };

    // Handle bill by change
    const handleBillByChange = (billBy) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                bill_by: billBy,
                // Clear weight fields when switching to dimension billing
                act_wt: billBy === 'Weight' ? prev.act_wt : '',
                // Clear dimension fields when switching to weight billing  
                wt_l: billBy === 'Dimention' ? prev.wt_l : '',
                wt_w: billBy === 'Dimention' ? prev.wt_w : '',
                wt_h: billBy === 'Dimention' ? prev.wt_h : '',
                // Retain existing charged weight if available, otherwise calculate
                charged_wt: prev.charged_wt || ''
            };

            // Only calculate charged weight if it's not already set or if we have the required data
            if (!newData.charged_wt) {
                if (billBy === 'Weight' && prev.act_wt) {
                    const chargedWeight = parseFloat(prev.act_wt) || 0;
                    newData.charged_wt = chargedWeight;
                } else if (billBy === 'Dimention' && prev.wt_l && prev.wt_w && prev.wt_h) {
                    const dimensionalWeight = bookingService.calculateDimensionalWeight(
                        prev.wt_l, 
                        prev.wt_w, 
                        prev.wt_h,
                        prev.transport || 'Surface'
                    );
                    newData.charged_wt = dimensionalWeight;
                }
            }

            return newData;
        });

        // Entering dimension billing: make the box rows match the package count
        if (billBy === 'Dimention') {
            const count = Math.max(1, parseInt(formData.no_of_pack, 10) || 1);
            setBoxes(prev => {
                let updated = [...prev];
                if (count > updated.length) {
                    while (updated.length < count) {
                        updated.push({ length: '', width: '', height: '', volumetric_weight: 0 });
                    }
                } else if (count < updated.length) {
                    updated = updated.slice(0, count);
                }
                return updated;
            });
        }
    };

    // Calculate charged weight
    const calculateChargedWeight = (field, value) => {
        // Only recalculate charged weight if the field value actually changed
        const currentValue = parseFloat(value) || 0;
        const existingValue = parseFloat(formData[field]) || 0;
        
        if (currentValue === existingValue) {
            return; // No change, don't recalculate
        }
        
        if (formData.bill_by === 'Weight' && field === 'act_wt') {
            setFormData(prev => ({ ...prev, charged_wt: currentValue }));
        } else if (formData.bill_by === 'Dimention') {
            const dimensions = { ...formData };
            dimensions[field] = currentValue;
            
            if (dimensions.wt_l && dimensions.wt_w && dimensions.wt_h) {
                const dimensionalWeight = bookingService.calculateDimensionalWeight(
                    dimensions.wt_l, 
                    dimensions.wt_w, 
                    dimensions.wt_h,
                    formData.transport
                );
                setFormData(prev => ({ ...prev, charged_wt: dimensionalWeight }));
            }
        }
    };

    // Debounced search function
    const debouncedSearch = useCallback((searchFunction, delay = 300) => {
        let timeoutId = null;
        
        return (...args) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            
            timeoutId = setTimeout(() => {
                searchFunction(...args);
            }, delay);
        };
    }, []);

    // Customer search functions with debouncing
    const searchCustomers = useCallback(async (searchText, isRecipient = false) => {
        if (searchText.length < 2) return;

        const setLoading = isRecipient ? setRecipientLoading : setShipperLoading;
        const setOptions = isRecipient ? setRecipientOptions : setShipperOptions;

        setLoading(true);
        try {
            const response = await bookingService.searchCustomers(searchText);
            if (response.success) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to search customers:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedSearchCustomers = useMemo(() => 
        debouncedSearch(searchCustomers), [debouncedSearch, searchCustomers]
    );

    // Third party customer search functions with debouncing
    const searchThirdPartyCustomers = useCallback(async (searchText) => {
        if (searchText.length < 2) return;

        setThirdPartyLoading(true);
        try {
            const response = await bookingService.searchCustomers(searchText);
            if (response.success) {
                setThirdPartyOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to search third party customers:', error);
        } finally {
            setThirdPartyLoading(false);
        }
    }, []);

    const debouncedSearchThirdPartyCustomers = useMemo(() => 
        debouncedSearch(searchThirdPartyCustomers), [debouncedSearch, searchThirdPartyCustomers]
    );



    // Load most frequently used addresses
    const loadFrequentAddresses = useCallback(async (isRecipient = false) => {
        // Determine which state setters to use based on isRecipient parameter
        const setLoading = isRecipient ? setRecipientAddressLoading : setShipperAddressLoading;
        const setOptions = isRecipient ? setRecipientAddressOptions : setShipperAddressOptions;

        setLoading(true);
        try {
            const response = await bookingService.searchAddresses('', 'both', 10);
            if (response.success) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to load frequent addresses:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Address search functions with debouncing
    const searchAddresses = useCallback(async (searchText, isRecipient = false) => {
        if (searchText.length < 2) {
            // If search text is less than 2 characters, load frequent addresses
            loadFrequentAddresses(isRecipient);
            return;
        }

        // Determine which state setters to use based on isRecipient parameter
        const setLoading = isRecipient ? setRecipientAddressLoading : setShipperAddressLoading;
        const setOptions = isRecipient ? setRecipientAddressOptions : setShipperAddressOptions;

        setLoading(true);
        try {
            const response = await bookingService.searchAddresses(searchText, 'both');
            if (response.success) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to search addresses:', error);
        } finally {
            setLoading(false);
        }
    }, [loadFrequentAddresses]);

    const debouncedSearchAddresses = useMemo(() => 
        debouncedSearch(searchAddresses), [debouncedSearch, searchAddresses]
    );

    // Load frequent addresses on component mount
    useEffect(() => {
        // Load frequent addresses for both shipper and recipient fields
        loadFrequentAddresses(false); // shipper
        loadFrequentAddresses(true);  // recipient
    }, [loadFrequentAddresses]);

    // Handle address selection
    const handleAddressSelect = (value, isRecipient = false) => {
        if (!value) return;

        const prefix = isRecipient ? 'to_' : 'frm_';
        const gstField = isRecipient ? 'recipient_gst_no' : 'shipper_gst_no';

        setFormData(prev => ({
            ...prev,
            [`${prefix}name`]: value.name || '',
            [`${prefix}company`]: value.company || '',
            [`${prefix}address`]: value.address || '',
            [`${prefix}city`]: value.city || '',
            [`${prefix}state`]: value.state || '',
            [`${prefix}pincode`]: value.pincode || '',
            [`${prefix}tel`]: value.mobile || '',
            [gstField]: value.gst_no || ''
        }));

        // Auto-populate pincode details if available
        if (value.pincode) {
            // Populate programmatically without triggering public pincode API
            handlePincodeChange(value.pincode, isRecipient, false);
        }
    };



    // Calculate rate - Define early to avoid hoisting issues
    const calculateRate = useCallback(async () => {
        if (!formData.TXT_ORI_ID || !formData.TXT_DES_ID || !formData.product_id || !formData.charged_wt) {
            showWarning('Please fill all required fields for rate calculation');
            return;
        }

        setCalculating(true);
        setBaseRateInfo(null); // Clear previous rate info
        
        try {
            // Get the billing customer ID
            const billingCustomerId = determineBillingCustomerId();

            const requestData = {
                billing_customer_id: billingCustomerId,
                transaction_mode: formData.payment_mode,
                from_city_id: formData.TXT_ORI_ID,
                to_city_id: formData.TXT_DES_ID,
                product_id: formData.product_id,
                weight: formData.charged_wt,
                bill_by: formData.bill_by
            };
            

            const response = await bookingService.calculateRate(requestData);

            if (response.success) {
                const rateData = response.data;
                const charges = rateData.charges;
                const rateDetails = rateData.rate_details;
                
                // Update form with calculated rates from backend
                // Backend now returns:
                // - basic_freight: DISPLAY value (tax-reduced for inclusive, full for exclusive)
                // - discounted_freight: DISPLAY value (tax-reduced for inclusive, discounted full for exclusive)
                // - backend_calculated_base: CALCULATION value (always use this for totals, NOT editable values)
                setFormData(prev => ({
                    ...prev,
                    // DISPLAY values (shown in editable fields - tax-reduced for inclusive)
                    basic_freight: charges.basic_freight || 0,
                    fuel_surcharge: charges.fuel_surcharge || 0,
                    discounted_basic_freight: charges.discounted_freight || 0,
                    discounted_fuel_surcharge: charges.discounted_fuel_surcharge || 0,

                    // STORED values (original amounts for database - always full amounts)
                    actual_basic_freight: charges.actual_basic_freight || 0,
                    actual_fuel_surcharge: charges.actual_fuel_surcharge || 0,

                    // BACKEND CALCULATION values (MUST use these, NOT the editable input values)
                    backend_discounted_freight: charges.backend_calculated_base || charges.discounted_freight || 0,
                    backend_discounted_fuel: charges.backend_calculated_fuel || charges.discounted_fuel_surcharge || 0,

                    handling_charges: charges.additional_charges?.handling || 0,
                    stat_charges: charges.additional_charges?.statistical || 0,
                    to_pay_surcharge: charges.additional_charges?.to_pay || 0,
                    cod_charges: charges.additional_charges?.cod_charges || 0,
                    sps_charges: charges.additional_charges?.special_service || 0,
                    value_surc: charges.additional_charges?.value_surcharge || 0,
                    cr_charges: charges.additional_charges?.carriers_risk || 0,
                    ser_tax: charges.tax_amount || charges.gst || 0,
                    total_amt: charges.base_total || charges.total_amount || 0,
                    grand_total: charges.grand_total || 0,
                    product_tax_calculation_type: rateDetails.tax_type || rateDetails.product_tax_calculation_type || 'inclusive'
                }));

                // Reset edit flags when new rates are calculated from backend
                setIsBasicFreightEdited(false);
                setIsFuelSurchargeEdited(false);
                setIsDiscountedFreightEdited(false);
                setIsDiscountedFuelEdited(false);

                // Store comprehensive rate info for display
                setBaseRateInfo({
                    // Discount-related info (for pricing calculations)
                    actual_basic_freight: charges.discounted_freight || 0,
                    actual_fuel_surcharge: charges.discounted_fuel_surcharge || charges.fuel_surcharge || 0,
                    fuel_discount: charges.fuel_discount || 0,
                    package_discount: charges.package_discount || 0,
                    charged_weight: charges.charged_weight || 0,
                    
                    // Rate details info (for rate calculation details display)
                    billing_customer: rateDetails.billing_customer,
                    billing_mode: rateDetails.billing_mode,
                    weight_used: rateData.weight_used,
                    zone_name: rateDetails.zone_name,
                    rate_type: rateDetails.rate_type,
                    product_type: rateDetails.product_type,
                    default_weight_range: rateDetails.default_weight_range,
                    base_rate: rateDetails.base_rate,
                    additional_charge_display: rateDetails.additional_charge_display,
                    excess_weight: rateDetails.excess_weight,
                    
                    // Legacy fields for compatibility
                    using_base_rate: rateDetails.using_base_rate || false,
                    base_rate_info: rateDetails.base_rate_info || '',
                    customer_details: rateData.customer_details,
                    discount_applied_for: rateData.discount_applied_for || rateDetails.billing_mode
                });

                // Show detailed success message
                const customerName = rateData.customer_details?.name || 'No customer selected';
                const customerCode = rateData.customer_details?.code || '';
                const discountApplied = charges.fuel_discount > 0 || charges.package_discount > 0;
                
                let successMessage = `Rate calculated successfully`;
                
                showSuccess(successMessage);
            } else {
                showError(response.message || 'Failed to calculate rate');
            }
        } catch (error) {
            console.error('Error calculating rate:', error);
            showError('Failed to calculate rate. Please try again.');
        } finally {
            setCalculating(false);
        }
    },
    [formData.TXT_ORI_ID, formData.TXT_DES_ID, formData.product_id, formData.charged_wt, formData.payment_mode, formData.bill_by, shipperCustomerId, recipientCustomerId, thirdPartyCustomerId, showError, showWarning, showSuccess]
);

    // Handle payment mode change
    const handlePaymentModeChange = (paymentMode) => {
        const previousPaymentMode = formData.payment_mode;
        
        // Determine the appropriate billing customer ID based on the new payment mode
        let newBillingCustomerId = null;
        let newBillingCustomerName = '';
        
        if (paymentMode === 'BILL TO SHIPPER') {
            newBillingCustomerId = shipperCustomerId;
            newBillingCustomerName = formData.frm_name || '';
        } else if (paymentMode === 'BILL TO RECEIPIENT') {
            newBillingCustomerId = recipientCustomerId;
            newBillingCustomerName = formData.to_name || '';
        } else if (paymentMode === 'BILL TO THIRD PARTY') {
            newBillingCustomerId = thirdPartyCustomerId;
            newBillingCustomerName = formData.third_party_customer_name || '';
        }
        
        // In edit mode, preserve existing customer data and only update billing customer
        if (isEditMode) {
            
            // Show warning about payment mode change in edit mode
            if (paymentMode !== previousPaymentMode) {
                showWarning(`Payment mode changed from "${previousPaymentMode}" to "${paymentMode}". This may affect billing and pricing calculations.`);
            }
            
            // Check if the new billing customer ID is null and show appropriate message
            if (!newBillingCustomerId) {
                showWarning(`No customer selected for "${paymentMode}". Please select a customer for this payment mode.`);
            }
            
            setFormData(prev => ({
                ...prev,
                payment_mode: paymentMode,
                billing_customer_id: newBillingCustomerId,
                billing_customer_name: newBillingCustomerName
            }));
            
            // Don't clear customer IDs in edit mode to preserve data
            return;
        }
        
        // For new bookings, clear other customer selections
        setFormData(prev => ({
            ...prev,
            payment_mode: paymentMode,
            third_party_customer_name: '',
            billing_customer_id: newBillingCustomerId,
            billing_customer_name: newBillingCustomerName
        }));
        
        // Only clear customer selections if payment mode actually changed (for new bookings)
        if (paymentMode !== previousPaymentMode) {
            if (paymentMode === 'BILL TO SHIPPER') {
                setRecipientCustomerId(null);
                setThirdPartyCustomerId(null);
                setSelectedThirdPartyCustomer(null);
                setRecipientOptions([]);
                setRecipientSearchText('');
                setThirdPartyOptions([]);
                setThirdPartySearchText('');
            } else if (paymentMode === 'BILL TO RECEIPIENT') {
                setShipperCustomerId(null);
                setThirdPartyCustomerId(null);
                setSelectedThirdPartyCustomer(null);
                setShipperOptions([]);
                setShipperSearchText('');
                setThirdPartyOptions([]);
                setThirdPartySearchText('');
            } else if (paymentMode === 'BILL TO THIRD PARTY') {
                setShipperCustomerId(null);
                setRecipientCustomerId(null);
                setShipperOptions([]);
                setShipperSearchText('');
                setRecipientOptions([]);
                setRecipientSearchText('');
            }
        }
    };

    // Handle customer selection for billing
    const handleCustomerSelect = async (value, isRecipient = false) => {
        if (!value) {
            // Clear customer data
            if (isRecipient) {
                setRecipientCustomerId(null);
            } else {
                setShipperCustomerId(null);
            }
            
            setFormData(prev => ({
                ...prev,
                [`${isRecipient ? 'to_' : 'frm_'}name`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}company`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}address`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}city`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}state`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}pincode`]: '',
                [`${isRecipient ? 'to_' : 'frm_'}tel`]: '',
                billing_customer_id: null,
                billing_customer_name: ''
            }));
            
            // Clear pincode info
            if (isRecipient) {
                setRecipientPincodeInfo(null);
            } else {
                setShipperPincodeInfo(null);
            }
            return;
        }

        const customer = value;
        const prefix = isRecipient ? 'to_' : 'frm_';

        // Set customer ID for tracking
        if (isRecipient) {
            setRecipientCustomerId(customer.id);
        } else {
            setShipperCustomerId(customer.id);
        }

        setFormData(prev => {
            const updatedData = {
                ...prev,
                [`${prefix}name`]: customer.cust_name,
                [`${prefix}company`]: customer.from_company || '',
                [`${prefix}address`]: customer.from_address || '',
                [`${prefix}city`]: customer.from_city || '',
                [`${prefix}state`]: customer.from_state || '',
                [`${prefix}pincode`]: customer.from_pincode || '',
                [`${prefix}tel`]: customer.from_mobile_no || '',
                billing_customer_id: customer.id,
                billing_customer_name: customer.cust_name
            };
            
            return updatedData;
        });

        // Auto-populate pincode details if available
        if (customer.from_pincode) {
            // Populate programmatically without triggering public pincode API
            handlePincodeChange(customer.from_pincode, isRecipient, false);
        }

        // Clear third party customer selection only if not in third party billing mode
        if (formData.payment_mode !== 'BILL TO THIRD PARTY') {
            setSelectedThirdPartyCustomer(null);
            setThirdPartyCustomerId(null);
            setThirdPartySearchText('');
            setThirdPartyOptions([]);
        }
    };

    // Handle third party customer selection
    const handleThirdPartyCustomerSelect = async (value) => {
        if (!value) {
            setThirdPartyCustomerId(null);
            setFormData(prev => ({
                ...prev,
                third_party_customer_name: '',
                billing_customer_id: null,
                billing_customer_name: ''
            }));
            setSelectedThirdPartyCustomer(null);
            setThirdPartySearchText('');
            return;
        }

        const customer = value;
        setThirdPartyCustomerId(customer.id);
        setSelectedThirdPartyCustomer(customer); // Set the selected customer object
        setFormData(prev => ({
            ...prev,
            third_party_customer_name: customer.cust_name,
            billing_customer_id: customer.id,
            billing_customer_name: customer.cust_name
        }));

        // Update search text to show the selected customer
        setThirdPartySearchText(`${customer.cust_name} (${customer.cust_code}) - ${customer.from_company || 'N/A'}`);

        // Clear shipper/recipient customer selections
        setShipperCustomerId(null);
        setRecipientCustomerId(null);
        setShipperOptions([]);
        setShipperSearchText('');
        setRecipientOptions([]);
        setRecipientSearchText('');
    };

    // Helper function to get billing customer name for display
    const getBillingCustomerName = () => {
        if (formData.payment_mode === 'BILL TO SHIPPER') {
            return formData.billing_customer_name || 'No Shipper Customer Selected';
        } else if (formData.payment_mode === 'BILL TO RECEIPIENT') {
            return formData.billing_customer_name || 'No Recipient Customer Selected';
        } else if (formData.payment_mode === 'BILL TO THIRD PARTY') {
            return formData.third_party_customer_name || 'No Third Party Customer Selected';
        }
        return 'No Customer Selected';
    };

    // Keyboard shortcuts for faster navigation
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (viewMode) return; // Disable shortcuts in view mode
            
            // Ctrl/Cmd + Enter to submit form
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                handleFormSubmit();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [viewMode]);

    // Utility function to clear product cache
    const clearProductCache = useCallback(() => {
        setProductCache(new Map());
        setErrorCache(new Map());
        setCurrentRoute(null);
        setProducts([]);
    }, []);

    // Function to retry loading products for current route
    const retryLoadProducts = useCallback(() => {
        if (currentRoute) {
            const [fromCityId, toCityId, billingId] = currentRoute.split('-');
            // Remove from error cache to allow retry
            setErrorCache(prev => {
                const newErrorCache = new Map(prev);
                newErrorCache.delete(currentRoute);
                return newErrorCache;
            });
            loadProducts(fromCityId, toCityId, billingId === 'any' ? null : billingId);
        }
    }, [currentRoute, loadProducts]);

    // Simplified route key calculation.
    //
    // The billing customer is part of the key because the offered packages depend
    // on it: switching from a customer with assigned packages to one without must
    // reload the list rather than reuse the previous customer's.
    const currentRouteKey = useMemo(() => {
        const fromCityId = formData.TXT_ORI_ID;
        const toCityId = formData.TXT_DES_ID;

        if (!fromCityId || !toCityId) {
            return null;
        }

        // Ensure consistent type handling - convert to numbers for comparison
        const fromId = Number(fromCityId);
        const toId = Number(toCityId);
        const billingId = determineBillingCustomerId() || 'any';

        // Only create route key if both IDs are valid numbers
        return !isNaN(fromId) && !isNaN(toId) ? `${fromId}-${toId}-${billingId}` : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formData.TXT_ORI_ID,
        formData.TXT_DES_ID,
        formData.payment_mode,
        formData.booking_type,
        formData.billing_customer_id,
        shipperCustomerId,
        recipientCustomerId,
        thirdPartyCustomerId,
    ]);

    // Track previous route key to detect actual changes
    const previousRouteKey = useRef(null);
    
    // Only update route key if it actually changed
    const stableRouteKey = useMemo(() => {
        if (currentRouteKey !== previousRouteKey.current) {
            // Clear error cache for the previous route when route changes
            if (previousRouteKey.current) {
                setErrorCache(prev => {
                    const newErrorCache = new Map(prev);
                    newErrorCache.delete(previousRouteKey.current);
                    return newErrorCache;
                });
            }
            previousRouteKey.current = currentRouteKey;
            return currentRouteKey;
        }
        return previousRouteKey.current;
    }, [currentRouteKey]);

    // Load products when route changes (simplified logic)
    useEffect(() => {
        // Don't trigger if we're loading a booking or already loading products
        if (loading || isLoadingBooking || loadingProducts) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (stableRouteKey) {
                const [fromCityId, toCityId, billingId] = stableRouteKey.split('-');
                loadProducts(fromCityId, toCityId, billingId === 'any' ? null : billingId);
            } else {
                // Only clear products if we have a valid route key that changed
                if (currentRoute) {
                    setProducts([]);
                    setCurrentRoute(null);
                }
            }
        }, 250); // debounce

        return () => clearTimeout(timeoutId);
    }, [stableRouteKey, loadProducts, currentRoute, loading, isLoadingBooking, loadingProducts]);

    // Clear product cache when component unmounts
    useEffect(() => {
        return () => {
            setProductCache(new Map());
            setErrorCache(new Map());
            setCurrentRoute(null);
            setProducts([]);
        };
    }, []);

    // Clear product cache when switching between different bookings
    useEffect(() => {
        if (bookingId) {
            setProductCache(new Map());
            setErrorCache(new Map());
            setCurrentRoute(null);
            setProducts([]);
        }
    }, [bookingId]);



    const handleFormSubmit = useCallback(async () => {
        if (submitting) return;

        // Check for billing customer ID only for credit bookings
        // Instant bookings don't require a billing customer
        if (formData.booking_type === 'credit') {
            const billingCustomerId = determineBillingCustomerId();

            if (!billingCustomerId) {
                console.error('Billing Customer ID missing:', {
                    payment_mode: formData.payment_mode,
                    shipperCustomerId,
                    recipientCustomerId,
                    thirdPartyCustomerId,
                    billing_customer_id: formData.billing_customer_id
                });
                showError('Billing Customer ID missing. Please select a customer for billing based on the transaction mode.');
                return;
            }
        }

        const validationResult = bookingService.validateBookingData(formData);
        if (!validationResult.isValid) {
            setErrors(validationResult.errors);
            showError('Please correct the form errors');
            return;
        }

        setSubmitting(true);
        try {            
            // Start with basic required fields
            const submitData = {
                awb_no: formData.awb_no,
                booking_date: formData.booking_date,
                booking_time: formData.booking_time,
                TXT_B_OFF_ID: formData.TXT_B_OFF_ID,
                TXT_ORI_ID: formData.TXT_ORI_ID,
                TXT_DES_ID: formData.TXT_DES_ID,
                transport: formData.transport,
                booking_type: formData.booking_type || 'credit',
                sa_no: formData.sa_no || '',

                // Send billing_customer_id for backend to set customer_id appropriately
                // For credit bookings: use determineBillingCustomerId()
                // For instant bookings: set to null (no customer mapping)
                billing_customer_id: formData.booking_type === 'instant' ? null : determineBillingCustomerId(),

                // Customer details - Shipper
                frm_name: formData.frm_name,
                frm_company: formData.frm_company || '',
                frm_address: formData.frm_address,
                frm_city: formData.frm_city,
                frm_state: formData.frm_state,
                frm_pincode: formData.frm_pincode,
                frm_tel: formData.frm_tel,
                shipper_gst_no: formData.shipper_gst_no || null,

                // Customer details - Recipient
                to_name: formData.to_name,
                to_company: formData.to_company || '',
                to_address: formData.to_address,
                to_city: formData.to_city,
                to_state: formData.to_state,
                to_pincode: formData.to_pincode,
                to_tel: formData.to_tel,
                recipient_gst_no: formData.recipient_gst_no || null,
                
                // Package details
                no_of_pack: parseInt(formData.no_of_pack) || 1,
                package_name: formData.package_name || '',
                bill_by: formData.bill_by || 'Weight',
                product_id: formData.product_id || null,
                charged_wt: parseFloat(formData.charged_wt) || (booking ? parseFloat(booking.billing?.charged_weight) || 0 : 0),
                dec_value: parseFloat(formData.dec_value) || 0,
                
                // Pricing
                actual_basic_freight: bookingService.roundToTwoDecimals(parseFloat(formData.actual_basic_freight) || 0),
                actual_fuel_surcharge: bookingService.roundToTwoDecimals(parseFloat(formData.actual_fuel_surcharge) || 0),
                basic_freight: bookingService.roundToTwoDecimals(parseFloat(formData.basic_freight) || 0),
                fuel_surcharge: bookingService.roundToTwoDecimals(parseFloat(formData.fuel_surcharge) || 0),
                handling_charges: bookingService.roundToTwoDecimals(parseFloat(formData.handling_charges) || 0),
                stat_charges: bookingService.roundToTwoDecimals(parseFloat(formData.stat_charges) || 0),
                to_pay_surcharge: bookingService.roundToTwoDecimals(parseFloat(formData.to_pay_surcharge) || 0),
                cod_charges: bookingService.roundToTwoDecimals(parseFloat(formData.cod_charges) || 0),
                sps_charges: bookingService.roundToTwoDecimals(parseFloat(formData.sps_charges) || 0),
                value_surc: bookingService.roundToTwoDecimals(parseFloat(formData.value_surc) || 0),
                cr_charges: bookingService.roundToTwoDecimals(parseFloat(formData.cr_charges) || 0),
                remote_area_charges: bookingService.roundToTwoDecimals(parseFloat(formData.remote_area_charges) || 0),
                ser_tax: bookingService.roundToTwoDecimals(parseFloat(formData.ser_tax) || 0),
                total_amt: bookingService.roundToTwoDecimals(parseFloat(formData.total_amt) || 0),
                grand_total: bookingService.roundToTwoDecimals(parseFloat(formData.grand_total) || 0)
            };

            // Handle weight and dimension fields - database requires ALL to be NOT NULL
            if (formData.act_wt && parseFloat(formData.act_wt) > 0) {
                submitData.act_wt = parseFloat(formData.act_wt);
            } else {
                submitData.act_wt = 0; // Database requires NOT NULL
            }
            
            // Send dimension fields
            if (formData.bill_by === 'Dimention' && boxes.length > 0) {
                // Multi-box mode: send boxes array, zero out legacy fields
                submitData.wt_l = 0;
                submitData.wt_w = 0;
                submitData.wt_h = 0;
                submitData.boxes = boxes.map(box => ({
                    length: parseFloat(box.length) || 0,
                    width: parseFloat(box.width) || 0,
                    height: parseFloat(box.height) || 0,
                }));
                submitData.no_of_pack = boxes.length;
            } else {
                // Weight-based or legacy single-dimension
                submitData.wt_l = formData.wt_l && parseFloat(formData.wt_l) > 0 ? parseFloat(formData.wt_l) : 0;
                submitData.wt_w = formData.wt_w && parseFloat(formData.wt_w) > 0 ? parseFloat(formData.wt_w) : 0;
                submitData.wt_h = formData.wt_h && parseFloat(formData.wt_h) > 0 ? parseFloat(formData.wt_h) : 0;
            }

            // Add payment mode or instant payment type based on booking type
            if (formData.booking_type === 'credit') {
                submitData.payment_mode = formData.payment_mode;
            } else if (formData.booking_type === 'instant') {
                submitData.instant_payment_type = formData.instant_payment_type;
            }

            const response = booking 
                ? await bookingService.updateBooking(booking.booking_id, submitData)
                : await bookingService.createBooking(submitData);

            if (response.success) {
                // Pass the booking data to the success callback
                handleFormSuccess(response.data);
            } else {
                showError(response.message || 'Failed to save booking');
                if (response.errors) {
                    setErrors(response.errors);
                }
            }
        } catch (error) {
            console.error('Error saving booking:', error);
            showError('Failed to save booking');
        } finally {
            setSubmitting(false);
        }
    }, [submitting, formData, booking, bookingId, isCreateMode, navigate, showError, showSuccess]);

    const handleFormSuccess = async (bookingData) => {
        const action = isCreateMode ? 'created' : 'updated';
        const successMessage = `Booking ${action} successfully`;
        
        try {
            // Show success message
            showSuccess(successMessage);
            
            // Only handle AWB functionality for new bookings
            if (isCreateMode) {
                const bookingIdToPrint = bookingData?.booking_id;
                
                if (bookingIdToPrint) {
                    // For new bookings: Open AWB directly in new tab (no modal)
                    openAwbDirectly(bookingIdToPrint);
                    showSuccess('AWB labels opened in new tab for printing');
                }
            }
            
            // Navigate back to list after a short delay
            setTimeout(() => {
                navigate('/employee/bookings');
            }, 3000);
            
        } catch (error) {
            console.error('Error in success flow:', error);
            showSuccess(successMessage);
            navigate('/employee/bookings');
        }
    };


    const openAwbDirectly = async (bookingId) => {
        try {
            // Generate label data
            const response = await bookingService.generateLabels(bookingId);
            if (!response.success) {
                showError('Failed to generate AWB labels');
                return;
            }

            const labelData = response.data;
            
            // Open new tab with AWB labels using the same logic as AwbLabels component
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                showWarning('Please allow popups to open AWB labels in new tab');
                return;
            }

            // Use the same HTML generation logic as AwbLabels component
            printWindow.document.write(getFullPrintHTML(labelData));
            printWindow.document.close();
            
            // Wait for images and content to load
            printWindow.addEventListener('load', () => {
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                }, 500);
            });

            // Fallback timeout in case load event doesn't fire
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 2000);
            
        } catch (error) {
            console.error('Error opening AWB in new tab:', error);
            showError('Failed to open AWB labels');
        }
    };

    // Reuse the exact same HTML generation logic from AwbLabels component
    const getFullPrintHTML = (data) => {
        const printStyles = `
            <style>
                @page { size: 4in 6in; margin: 0.05in; }
                html, body { margin: 0; padding: 0; width: 100%; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: Arial, sans-serif; }
                body { display: block; background: white; }
                /* Page-sized container for each label - 4x6 thermal */
                .label { width: 3.9in; height: 5.9in; overflow: hidden; page-break-after: always; position: relative; padding: 0.02in; box-sizing: border-box; }
                .label:last-child { page-break-after: auto; }
                /* Label content */
                .label-inner { width: 100%; height: 100%; box-sizing: border-box; font-size: 9pt; line-height: 1.2; position: relative; display: flex; flex-direction: column; }
                .label-inner, .label-inner p, .label-inner div, .label-inner span { font-weight: 700; color: #000; }
                /* Logo at top - maximized */
                .top-logo { text-align: center; margin-bottom: 2px; }
                .top-logo img { max-height: 55px; max-width: 90%; width: auto; object-fit: contain; }
                /* Header */
                .header { margin-bottom: 2px; text-align: center; }
                .header h3 { font-size: 11pt; font-weight: 900; margin: 0 0 1px 0; line-height: 1.1; }
                .header p { font-size: 8pt; font-weight: 700; margin: 0; line-height: 1.1; }
                /* Main content box */
                .boxed { border: 2px solid #000; padding: 3px; margin-bottom: 2px; }
                /* Address sections */
                .section { margin-bottom: 2px; padding: 0; }
                .section-title { font-weight: 900; color: #000; margin-bottom: 1px; font-size: 9pt; text-transform: uppercase; }
                .section-content { font-size: 9pt; line-height: 1.25; font-weight: 500; }
                /* Info box */
                .info-box { border: 2px solid #000; padding: 2px; margin-bottom: 2px; text-align: center; background: white; }
                .info-box p { font-size: 8pt; font-weight: 900; margin: 1px 0; line-height: 1.2; }
                .info-box .large { font-size: 9pt; font-weight: 900; }
                /* Tracking numbers */
                .tracking-box { border: 3px solid #000; padding: 3px; margin: 2px 0; text-align: center; background: white; }
                .tracking-box p { font-size: 8pt; font-weight: 900; margin: 1px 0; }
                .tracking-box h3 { font-size: 14pt; font-weight: 900; margin: 2px 0; letter-spacing: 0.5px; }
                /* Barcode section */
                .barcode-section { text-align: center; margin-top: 3px; padding: 2px 0; }
                .barcode-section img { width: 90%; height: auto; max-height: 65px; }
                .barcode-section p { font-size: 11pt; font-weight: 900; margin: 2px 0; letter-spacing: 0.5px; }
                /* Package number at bottom */
                .package-number { text-align: center; padding: 3px 0; font-size: 9pt; font-weight: 900; border-top: 2px solid #000; margin-top: auto; }
                /* Meta info */
                .meta-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 7pt; font-weight: 700; }
                /* Tax info compact */
                .tax-info { font-size: 7pt; line-height: 1.1; }
                /* Ensure content fits */
                .label, .label-inner, .section { page-break-inside: avoid; }
                /* Make all text darker for thermal printing */
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            </style>
        `;

        let labelsHTML = '';
        for (let i = 1; i <= data.no_of_pack; i++) {
            labelsHTML += `
                <div class="label">
                    <div class="label-inner">
                        <!-- Logo at Top - Maximized -->
                        <div class="top-logo">
                            <img src="/admin/assets/images/logo.png" alt="SunEx Courier Logo" onerror="this.style.display='none'" />
                        </div>

                        <!-- Header Section -->
                        <div class="header">
                            <h3>${data.origin} To ${data.destination}</h3>
                            <p>DATE: ${new Date(data.booking_date).toLocaleDateString('en-GB')}</p>
                        </div>

                        <!-- Main Tracking Number -->
                        <div class="tracking-box">
                            <p>AWB TRACKING NO</p>
                            <h3>${data.awb_no}</h3>
                            <p>SUB NO: ${data.awb_no}-${i}</p>
                        </div>

                        <!-- Shipper Section -->
                        <div class="boxed">
                            <div class="section">
                                <div class="section-title">FROM:</div>
                                <div class="section-content">
                                    <strong>${data.shipper.name}</strong><br/>
                                    ${data.shipper.company ? data.shipper.company + '<br/>' : ''}
                                    ${data.shipper.address}<br/>
                                    ${data.shipper.city}, ${data.shipper.state} - ${data.shipper.pincode}<br/>
                                    <strong>Ph: ${data.shipper.mobile}</strong>
                                    ${data.shipper.gst_no ? '<br/><strong>GST: ' + data.shipper.gst_no + '</strong>' : ''}
                                </div>
                            </div>
                        </div>

                        <!-- Recipient Section -->
                        <div class="boxed">
                            <div class="section">
                                <div class="section-title">TO:</div>
                                <div class="section-content" style="font-weight: 900;">
                                    <strong>${data.recipient.name}</strong><br/>
                                    ${data.recipient.company ? data.recipient.company + '<br/>' : ''}
                                    ${data.recipient.address}<br/>
                                    ${data.recipient.city}, ${data.recipient.state} - ${data.recipient.pincode}<br/>
                                    <strong>Ph: ${data.recipient.mobile}</strong>
                                    ${data.recipient.gst_no ? '<br/><strong>GST: ' + data.recipient.gst_no + '</strong>' : ''}
                                </div>
                            </div>
                        </div>

                        <!-- Package Info -->
                        <div class="info-box">
                            <p><strong>PRODUCT:</strong> ${data.package_name}</p>
                            <p><strong>PCS:</strong> ${data.no_of_pack} | <strong>WT:</strong> ${data.charged_weight} kg</p>
                            ${(i === 1 && data.payment_info) ? `
                                <p style="margin-top: 2px;"><strong>${data.payment_info.payment_type === 'paid' ? 'PAID' : 'TO PAY'}: ₹${data.payment_info.grand_total}</strong></p>
                                <div class="tax-info">
                                    <span>Base: ₹${data.payment_info.total_amount}</span> |
                                    <span>SGST: ₹${data.payment_info.sgst}</span> |
                                    <span>CGST: ₹${data.payment_info.cgst}</span>
                                </div>
                            ` : ''}
                        </div>

                        <!-- Barcode -->
                        <div class="barcode-section">
                            <img src="data:image/png;base64,${data.barcode_data}"
                                 alt="Barcode" />
                            <p>${data.awb_no}</p>
                        </div>

                        <!-- Meta Info -->
                        <div class="meta-row">
                            <span>REF: ${data.ref_no || 'N/A'}</span>
                            <span>${data.current_date || new Date(data.booking_date).toLocaleDateString('en-GB')}</span>
                        </div>

                        <!-- Package Number -->
                        <div class="package-number">
                            PKG ${i} of ${data.no_of_pack}
                        </div>
                    </div>
                </div>
            `;
        }

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>AWB Labels - ${data.awb_no}</title>
                ${printStyles}
            </head>
            <body>
                ${labelsHTML}
            </body>
            </html>
        `;
    };

    const handleFormCancel = () => {
        navigate('/employee/bookings');
    };

    const getPageTitle = () => {
        if (isCreateMode) return 'Create New Booking';
        if (isViewMode) return 'View Booking';
        return 'Edit Booking';
    };

    const getBreadcrumbs = () => {
        const breadcrumbs = [
            <Link
                key="bookings"
                color="inherit"
                href="/employee/bookings"
                onClick={(e) => {
                    e.preventDefault();
                    navigate('/employee/bookings');
                }}
            >
                Bookings
            </Link>
        ];

        if (isCreateMode) {
            breadcrumbs.push(<Typography key="create" color="text.primary">Create New</Typography>);
        } else if (booking) {
            breadcrumbs.push(
                <Typography key="booking" color="text.primary">
                    {isViewMode ? 'View' : 'Edit'} - {booking.awb_no}
                </Typography>
            );
        }

        return breadcrumbs;
    };

    // Render general section
    const renderGeneralSection = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="AWB Number"
                    value={formData.awb_no}
                    onChange={(e) => handleInputChange('awb_no', e.target.value)}
                    disabled={viewMode || !!booking}
                    error={!!errors.awb_no}
                    helperText={errors.awb_no || (booking ? 'AWB number cannot be changed after creation' : '')}
                    InputProps={{
                        endAdornment: !viewMode && !booking && (
                            <InputAdornment position="end">
                                <IconButton onClick={generateAwbNumber} size="small">
                                    <RefreshIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    label="Booking Date"
                    type="date"
                    value={formData.booking_date}
                    onChange={(e) => handleInputChange('booking_date', e.target.value)}
                    disabled={viewMode}
                    error={!!errors.booking_date}
                    helperText={errors.booking_date}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    label="Booking Time"
                    type="time"
                    value={formData.booking_time}
                    onChange={(e) => handleInputChange('booking_time', e.target.value)}
                    disabled={viewMode}
                    error={!!errors.booking_time}
                    helperText={errors.booking_time}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={4}>
                <Autocomplete
                    options={bookingOffices}
                    getOptionLabel={(option) => option?.name || 'Unknown Office'}
                    value={bookingOffices.find(office => office?.id === formData.TXT_B_OFF_ID) || null}
                    onChange={(_, newValue) => handleInputChange('TXT_B_OFF_ID', newValue !== null ? newValue.id : '')}
                    disabled={viewMode}
                    loading={loadingBookingOffices}
                    filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter(option => {
                            const name = option?.name || '';
                            const city = option?.city || '';
                            return name.toLowerCase().includes(searchTerm) ||
                                   city.toLowerCase().includes(searchTerm);
                        });
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Booking Office"
                            error={!!errors.TXT_B_OFF_ID}
                            helperText={errors.TXT_B_OFF_ID}
                            required
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <BusinessIcon color="action" sx={{ ml: 1, mr: -0.5 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {loadingBookingOffices ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={`booking-office-${option?.id}`} {...otherProps}>
                                <Box>
                                    <Typography variant="body1">
                                        {option?.name || 'Unknown Office'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {option?.type === 'branch' ? 'Branch Office' : 
                                         option?.type === 'franchise' ? `Franchise - ${option?.city || 'N/A'}` : 
                                         'Unknown Type'}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <Autocomplete
                    options={cities}
                    getOptionLabel={(option) => `${option.city_name} (${option.city_short || 'No Code'})`}
                    value={cities.find(city => city.city_id === formData.TXT_ORI_ID) || null}
                    onChange={(_, newValue) => handleInputChange('TXT_ORI_ID', newValue?.city_id || '')}
                    disabled={viewMode}
                    loading={loadingCities}
                    filterOptions={filterCityOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="From City"
                            error={!!errors.TXT_ORI_ID}
                            helperText={errors.TXT_ORI_ID}
                            required
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <LocationCityIcon color="action" sx={{ ml: 1, mr: -0.5 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={`from-city-${option.city_id}`} {...otherProps}>
                                <Box>
                                    <Typography variant="body1">
                                        {option.city_name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Code: {option.city_short || 'N/A'}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <Autocomplete
                    options={cities}
                    getOptionLabel={(option) => `${option.city_name} (${option.city_short || 'No Code'})`}
                    value={cities.find(city => city.city_id === formData.TXT_DES_ID) || null}
                    onChange={(_, newValue) => handleInputChange('TXT_DES_ID', newValue?.city_id || '')}
                    disabled={viewMode}
                    loading={loadingCities}
                    filterOptions={filterCityOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="To City"
                            error={!!errors.TXT_DES_ID}
                            helperText={errors.TXT_DES_ID}
                            required
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <LocationCityIcon color="action" sx={{ ml: 1, mr: -0.5 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={`to-city-${option.city_id}`} {...otherProps}>
                                <Box>
                                    <Typography variant="body1">
                                        {option.city_name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Code: {option.city_short || 'N/A'}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <FormControl component="fieldset" error={!!errors.transport}>
                    <Typography variant="body2" gutterBottom>Transport Mode</Typography>
                    <RadioGroup
                        row
                        value={formData.transport}
                        onChange={(e) => handleInputChange('transport', e.target.value)}
                    >
                        <FormControlLabel
                            value="Surface"
                            control={<Radio />}
                            label="Surface"
                            disabled={viewMode}
                        />
                        <FormControlLabel
                            value="Air"
                            control={<Radio />}
                            label="Air"
                            disabled={viewMode}
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
                <FormControl component="fieldset" error={!!errors.booking_type}>
                    <Typography variant="body2" gutterBottom>Booking Type</Typography>
                    <RadioGroup
                        row
                        value={formData.booking_type}
                        onChange={(e) => handleInputChange('booking_type', e.target.value)}
                    >
                        <FormControlLabel
                            value="credit"
                            control={<Radio />}
                            label="Credit Booking"
                            disabled={viewMode}
                        />
                        <FormControlLabel
                            value="instant"
                            control={<Radio />}
                            label="Instant Booking"
                            disabled={viewMode}
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>

            {formData.booking_type === 'credit' && (
                <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" error={!!errors.payment_mode}>
                        <Typography variant="body2" gutterBottom>Payment Mode</Typography>
                        <RadioGroup
                            row
                            value={formData.payment_mode}
                            onChange={(e) => handleInputChange('payment_mode', e.target.value)}
                        >
                            <FormControlLabel
                                value="BILL TO SHIPPER"
                                control={<Radio />}
                                label="Bill to Shipper"
                                disabled={viewMode}
                            />
                            <FormControlLabel
                                value="BILL TO RECEIPIENT"
                                control={<Radio />}
                                label="Bill to Recipient"
                                disabled={viewMode}
                            />
                            <FormControlLabel
                                value="BILL TO THIRD PARTY"
                                control={<Radio />}
                                label="Bill to Third Party"
                                disabled={viewMode}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            )}

            {formData.booking_type === 'instant' && (
                <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" error={!!errors.instant_payment_type}>
                        <Typography variant="body2" gutterBottom>Payment Type</Typography>
                        <RadioGroup
                            row
                            value={formData.instant_payment_type}
                            onChange={(e) => handleInputChange('instant_payment_type', e.target.value)}
                        >
                            <FormControlLabel
                                value="to_pay"
                                control={<Radio />}
                                label="To Pay (Recipient)"
                                disabled={viewMode}
                            />
                            <FormControlLabel
                                value="paid"
                                control={<Radio />}
                                label="Paid (Shipper)"
                                disabled={viewMode}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            )}

            {formData.booking_type === 'credit' && formData.payment_mode === 'BILL TO THIRD PARTY' && (
                <Grid item xs={12} md={8}>
                    {viewMode ? (
                        <TextField
                            fullWidth
                            label="Third Party Billing Customer"
                            value={formData.third_party_customer_name || (formData.third_party_customer_id ? `Customer ID: ${formData.third_party_customer_id}` : 'No customer selected')}
                            disabled
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                            }}
                            helperText="Third party customer for billing (read-only in view mode)"
                        />
                    ) : (
                        <Autocomplete
                            options={thirdPartyOptions}
                            getOptionLabel={(option) => `${option.cust_name} (${option.cust_code}) - ${option.from_company || 'N/A'}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            loading={thirdPartyLoading}
                            disabled={viewMode}
                            value={selectedThirdPartyCustomer}
                            inputValue={thirdPartySearchText}
                            onInputChange={(event, newInputValue) => {
                                setThirdPartySearchText(newInputValue);
                                debouncedSearchThirdPartyCustomers(newInputValue);
                            }}
                            onChange={(event, value) => {
                                handleThirdPartyCustomerSelect(value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Third Party Billing Customer"
                                    placeholder="Search and select customer for billing..."
                                    error={!!errors.third_party_customer_id}
                                    helperText={errors.third_party_customer_id || 'Select the customer who will be billed for this shipment'}
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                        endAdornment: thirdPartyLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {option.cust_name} ({option.cust_code})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.from_company} • {option.from_email}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />
                    )}
                </Grid>
            )}

            <Grid item xs={12} md={formData.payment_mode === 'BILL TO THIRD PARTY' ? 4 : 4}>
                <TextField
                    fullWidth
                    label="Reference No"
                    value={formData.sa_no}
                    onChange={(e) => handleInputChange('sa_no', e.target.value)}
                    disabled={viewMode}
                    placeholder="Optional"
                />
            </Grid>
        </Grid>
    );

    // Render customer section
    const renderCustomerSection = (title, prefix, isRecipient = false) => {
        const nameField = `${prefix}name`;
        const companyField = `${prefix}company`;
        const addressField = `${prefix}address`;
        const cityField = `${prefix}city`;
        const stateField = `${prefix}state`;
        const pincodeField = `${prefix}pincode`;
        const telField = `${prefix}tel`;

        const searchText = isRecipient ? recipientSearchText : shipperSearchText;
        const setSearchText = isRecipient ? setRecipientSearchText : setShipperSearchText;
        const options = isRecipient ? recipientOptions : shipperOptions;
        const loading = isRecipient ? recipientLoading : shipperLoading;
        const pincodeInfo = isRecipient ? recipientPincodeInfo : shipperPincodeInfo;

        // Determine which search fields to show based on booking type and payment mode
        // This ensures users follow the correct workflow based on billing requirements
        const shouldShowCustomerSearch = () => {
            // For instant bookings, never show customer search - use booking history only
            if (formData.booking_type === 'instant') {
                return false;
            }

            // For credit bookings, follow the existing logic
            if (formData.payment_mode === 'BILL TO SHIPPER') {
                // When billing to shipper:
                // - Shipper section: show customer search (must select from existing customers)
                // - Recipient section: hide customer search (can use previous addresses)
                return !isRecipient;
            } else if (formData.payment_mode === 'BILL TO RECEIPIENT') {
                // When billing to recipient:
                // - Shipper section: hide customer search (can use previous addresses)
                // - Recipient section: show customer search (must select from existing customers)
                return isRecipient;
            } else if (formData.payment_mode === 'BILL TO THIRD PARTY') {
                // When billing to third party:
                // - Both sections: hide customer search (must use previous addresses)
                return false;
            }
            return true; // Default: show both for other cases
        };

        const shouldShowAddressSearch = () => {
            // For instant bookings, always show address search (booking history)
            if (formData.booking_type === 'instant') {
                return true;
            }

            // For credit bookings, follow the existing logic
            if (formData.payment_mode === 'BILL TO SHIPPER') {
                // When billing to shipper:
                // - Shipper section: hide address search (must use customer search)
                // - Recipient section: show address search (can use previous addresses)
                return isRecipient;
            } else if (formData.payment_mode === 'BILL TO RECEIPIENT') {
                // When billing to recipient:
                // - Shipper section: show address search (can use previous addresses)
                // - Recipient section: hide address search (must use customer search)
                return !isRecipient;
            } else if (formData.payment_mode === 'BILL TO THIRD PARTY') {
                // When billing to third party:
                // - Both sections: show address search (must use previous addresses)
                return true;
            }
            return true; // Default: show both for other cases
        };

        return (
            <>
                <Grid container spacing={3}>
                    {/* Customer Search */}
                    {shouldShowCustomerSearch() && (
                        <Grid item xs={12}>
                            <Autocomplete
                                options={options}
                                getOptionLabel={(option) => `${option.cust_name} (${option.cust_code}) - ${option.from_company || 'N/A'}`}
                                loading={loading}
                                disabled={viewMode}
                                inputValue={searchText}
                                onInputChange={(event, newInputValue) => {
                                    setSearchText(newInputValue);
                                    debouncedSearchCustomers(newInputValue, isRecipient);
                                }}
                                onChange={(event, value) => handleCustomerSelect(value, isRecipient)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`Search ${title}`}
                                        placeholder="Type to search existing customers..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                            endAdornment: loading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {option.cust_name} ({option.cust_code})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.from_company} • {option.from_email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Grid>
                    )}

                    {/* Address Search */}
                    {shouldShowAddressSearch() && (
                        <>

                            <Grid item xs={12}>
                                <Autocomplete
                                    options={isRecipient ? recipientAddressOptions : shipperAddressOptions}

                                    getOptionLabel={(option) => option.display_text || `${option.name} - ${option.address}`}
                                    loading={isRecipient ? recipientAddressLoading : shipperAddressLoading}
                                    disabled={viewMode}
                                    inputValue={isRecipient ? recipientAddressSearchText : shipperAddressSearchText}
                                    filterOptions={(options) => options} // Disable default filtering since we filter on backend
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    noOptionsText="No addresses found. Start typing to search..."
                                    onFocus={() => {
                                        // Load frequent addresses when field is focused
                                        const currentOptions = isRecipient ? recipientAddressOptions : shipperAddressOptions;
                                        if (currentOptions.length === 0) {
                                            loadFrequentAddresses(isRecipient);
                                        }
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        if (isRecipient) {
                                            setRecipientAddressSearchText(newInputValue);
                                            debouncedSearchAddresses(newInputValue, true);
                                        } else {
                                            setShipperAddressSearchText(newInputValue);
                                            debouncedSearchAddresses(newInputValue, false);
                                        }
                                    }}
                                    onChange={(event, value) => handleAddressSelect(value, isRecipient)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={`Search Previous ${title} Addresses`}
                                            placeholder="Type to search from previous bookings..."
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: <LocationOnIcon color="action" sx={{ mr: 1 }} />,
                                                endAdornment: (isRecipient ? recipientAddressLoading : shipperAddressLoading) ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.company && option.company !== option.name ? `${option.company} • ` : ''}{option.address}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {option.city}, {option.state} - {option.pincode}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    {(shouldShowCustomerSearch() || shouldShowAddressSearch()) && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Or enter details manually:
                            </Typography>
                        </Grid>
                    )}

                    {/* Manual Entry Fields */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Contact Person Name"
                            value={formData[nameField]}
                            onChange={(e) => handleInputChange(nameField, e.target.value)}
                            disabled={viewMode}
                            error={!!errors[nameField]}
                            helperText={errors[nameField]}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Company/Division Name"
                            value={formData[companyField]}
                            onChange={(e) => handleInputChange(companyField, e.target.value)}
                            disabled={viewMode}
                            error={!!errors[companyField]}
                            helperText={errors[companyField]}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address"
                            value={formData[addressField]}
                            onChange={(e) => handleInputChange(addressField, e.target.value)}
                            disabled={viewMode}
                            multiline
                            rows={2}
                            error={!!errors[addressField]}
                            helperText={errors[addressField]}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Pin Code"
                            value={formData[pincodeField]}
                            onChange={(e) => handlePincodeChange(e.target.value, isRecipient, true)}
                            disabled={viewMode}
                            error={!!errors[pincodeField]}
                            placeholder="Enter 6-digit pincode"
                            helperText={
                                errors[pincodeField] || 
                                (pincodeLoading[isRecipient ? 'recipient' : 'shipper'] ? 'Fetching location details...' : '') ||
                                (formData[pincodeField] && formData[pincodeField].length < 6 ? 'Enter 6-digit pincode' : '')
                            }
                            inputProps={{ maxLength: 6 }}
                            required
                            InputProps={{
                                endAdornment: pincodeLoading[isRecipient ? 'recipient' : 'shipper'] && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {pincodeInfo && pincodeInfo.postOffices && pincodeInfo.postOffices.length > 1 ? (
                            <Autocomplete
                                options={pincodeInfo.postOffices}
                                getOptionLabel={(option) => option.name}
                                value={pincodeInfo.postOffices.find(po => po.name === formData[cityField]) || null}
                                onChange={(event, newValue) => {
                                    handleInputChange(cityField, newValue ? newValue.name : '');
                                }}
                                disabled={viewMode}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="City/Area"
                                        error={!!errors[cityField]}
                                        helperText={errors[cityField] || 'Select from available areas'}
                                        required
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2">
                                                {option.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.branchType} • {option.deliveryStatus}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="City"
                                value={formData[cityField]}
                                onChange={(e) => handleInputChange(cityField, e.target.value)}
                                disabled={viewMode || !!pincodeInfo}
                                error={!!errors[cityField]}
                                helperText={errors[cityField] || (pincodeInfo ? 'Auto-populated from pincode' : '')}
                                required
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="State"
                            value={formData[stateField]}
                            onChange={(e) => handleInputChange(stateField, e.target.value)}
                            disabled={viewMode || !!pincodeInfo}
                            error={!!errors[stateField]}
                            helperText={errors[stateField]}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            label="Mobile No"
                            value={formData[telField]}
                            onChange={(e) => handleInputChange(telField, e.target.value)}
                            disabled={viewMode}
                            error={!!errors[telField]}
                            helperText={errors[telField]}
                            placeholder="Enter 10-digit mobile number"
                            required
                        />
                    </Grid>

                    {/* GST Number for Instant Bookings */}
                    {formData.booking_type === 'instant' && (
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="GST Number"
                                value={formData[isRecipient ? 'recipient_gst_no' : 'shipper_gst_no']}
                                onChange={(e) => handleInputChange(isRecipient ? 'recipient_gst_no' : 'shipper_gst_no', e.target.value)}
                                disabled={viewMode}
                                error={!!errors[isRecipient ? 'recipient_gst_no' : 'shipper_gst_no']}
                                helperText={
                                    errors[isRecipient ? 'recipient_gst_no' : 'shipper_gst_no'] ||
                                    (formData.instant_payment_type === 'paid' && !isRecipient ? 'Required for Paid bookings' :
                                     formData.instant_payment_type === 'to_pay' && isRecipient ? 'Required for To Pay bookings' :
                                     'Optional')
                                }
                                placeholder="Enter GST number"
                                required={
                                    (formData.instant_payment_type === 'paid' && !isRecipient) ||
                                    (formData.instant_payment_type === 'to_pay' && isRecipient)
                                }
                            />
                        </Grid>
                    )}

                    {/* Pincode Info Display */}
                    {pincodeInfo && (
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    <strong>Location Info:</strong> {pincodeInfo.city}, {pincodeInfo.district}, {pincodeInfo.state}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    {pincodeInfo.postOffices?.slice(0, 3).map((po, index) => (
                                        <Chip key={index} label={po.name} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </>
        );
    };

    // Render billing section
    const renderBillingSection = () => {
        return (
            <>
                <Grid container spacing={3}>
                    {/* Package Details */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="No. of Packages"
                            type="number"
                            value={formData.no_of_pack}
                            onChange={(e) => handleNumPackagesChange(e.target.value)}
                            disabled={viewMode}
                            error={!!errors.no_of_pack}
                            helperText={errors.no_of_pack || (formData.bill_by === 'Dimention' ? 'Sets the number of box dimension rows below' : '')}
                            inputProps={{ min: 1 }}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {!showCustomPackageDesc ? (
                            <FormControl fullWidth error={!!errors.package_name} required disabled={viewMode}>
                                <InputLabel>Package Description</InputLabel>
                                <Select
                                    value={packageDescriptionOptions.includes(formData.package_name) ? formData.package_name : ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === 'Others') {
                                            setShowCustomPackageDesc(true);
                                            handleInputChange('package_name', '');
                                        } else {
                                            handleInputChange('package_name', value);
                                        }
                                    }}
                                    label="Package Description"
                                >
                                    {packageDescriptionOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.package_name && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                        {errors.package_name}
                                    </Typography>
                                )}
                            </FormControl>
                        ) : (
                            <TextField
                                fullWidth
                                label="Package Description (Custom)"
                                value={formData.package_name}
                                onChange={(e) => handleInputChange('package_name', e.target.value)}
                                disabled={viewMode}
                                error={!!errors.package_name}
                                helperText={
                                    errors.package_name ||
                                    `${(formData.package_name || '').length}/200 characters`
                                }
                                placeholder="Enter custom package description"
                                required
                                inputProps={{
                                    maxLength: 200
                                }}
                                InputProps={{
                                    endAdornment: !viewMode && (
                                        <InputAdornment position="end">
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setShowCustomPackageDesc(false);
                                                    handleInputChange('package_name', '');
                                                }}
                                            >
                                                Back to Options
                                            </Button>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                        {!errors.package_name && formData.package_name && !showCustomPackageDesc && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Tip: Select "Others" to enter a custom description
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Declared Value (₹)"
                            type="number"
                            value={formData.dec_value}
                            onChange={(e) => handleInputChange('dec_value', parseFloat(e.target.value) || 0)}
                            disabled={viewMode}
                            error={!!errors.dec_value}
                            helperText={errors.dec_value}
                            inputProps={{ min: 0, step: 0.01 }}
                            required
                        />
                    </Grid>

                    {/* Billing Method */}
                    <Grid item xs={12}>
                        <FormControl component="fieldset" error={!!errors.bill_by}>
                            <Typography variant="body2" gutterBottom>Billing Method</Typography>
                            <RadioGroup
                                row
                                value={formData.bill_by}
                                onChange={(e) => handleInputChange('bill_by', e.target.value)}
                            >
                                <FormControlLabel 
                                    value="Weight" 
                                    control={<Radio />} 
                                    label="By Weight" 
                                    disabled={viewMode}
                                />
                                <FormControlLabel 
                                    value="Dimention" 
                                    control={<Radio />} 
                                    label="By Dimension" 
                                    disabled={viewMode}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Weight/Dimension Fields */}
                    {formData.bill_by === 'Weight' ? (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Actual Weight (kg)"
                                type="number"
                                value={formData.act_wt}
                                onChange={(e) => handleInputChange('act_wt', e.target.value)}
                                disabled={viewMode}
                                error={!!errors.act_wt}
                                helperText={errors.act_wt}
                                inputProps={{ min: 0.1, step: 0.1 }}
                                required
                            />
                        </Grid>
                    ) : (
                        <>
                            {/* Actual Weight - always needed for comparison */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Actual Weight (kg)"
                                    type="number"
                                    value={formData.act_wt}
                                    onChange={(e) => handleInputChange('act_wt', e.target.value)}
                                    disabled={viewMode}
                                    error={!!errors.act_wt}
                                    helperText={errors.act_wt}
                                    inputProps={{ min: 0.1, step: 0.1 }}
                                    required
                                />
                            </Grid>

                            {/* Multi-box dimension rows */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Box Dimensions
                                </Typography>
                                {boxes.map((box, index) => (
                                    <Grid container spacing={1} key={index} sx={{ mb: 1 }} alignItems="center">
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`Box ${index + 1} - L (cm)`}
                                                type="number"
                                                value={box.length}
                                                onChange={(e) => handleBoxChange(index, 'length', e.target.value)}
                                                disabled={viewMode}
                                                inputProps={{ min: 0.1, step: 0.1 }}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`W (cm)`}
                                                type="number"
                                                value={box.width}
                                                onChange={(e) => handleBoxChange(index, 'width', e.target.value)}
                                                disabled={viewMode}
                                                inputProps={{ min: 0.1, step: 0.1 }}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`H (cm)`}
                                                type="number"
                                                value={box.height}
                                                onChange={(e) => handleBoxChange(index, 'height', e.target.value)}
                                                disabled={viewMode}
                                                inputProps={{ min: 0.1, step: 0.1 }}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                                Vol: {box.volumetric_weight} kg
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Summary */}
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    Packages: {boxes.length} &nbsp;|&nbsp;
                                    Total Vol. Weight: {boxes.reduce((s, b) => s + (b.volumetric_weight || 0), 0).toFixed(2)} kg &nbsp;|&nbsp;
                                    Actual Weight: {parseFloat(formData.act_wt) || 0} kg &nbsp;|&nbsp;
                                    <strong>Charged Weight: {formData.charged_wt} kg</strong>
                                    {' '}(higher of volumetric vs actual)
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {/* Charged Weight Display */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Charged Weight (kg)"
                            value={formData.charged_wt}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                            helperText="Auto-calculated based on billing method"
                        />
                    </Grid>

                    {/* Product Selection */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.product_id}>
                            <InputLabel>Product/Service</InputLabel>
                            <Select
                                value={formData.product_id}
                                onChange={(e) => handleInputChange('product_id', e.target.value)}
                                disabled={viewMode || loadingProducts}
                                label="Product/Service"
                            >
                                {products.map(product => (
                                    <MenuItem key={product.id} value={product.id}>
                                        {product.product_name}{product.product_description ? ` - ${product.product_description}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            {loadingProducts && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={12} />
                                    Loading available products...
                                </Typography>
                            )}
                            {!loadingProducts && !products.length && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {formData.TXT_ORI_ID && formData.TXT_DES_ID 
                                            ? (errorCache.has(`${formData.TXT_ORI_ID}-${formData.TXT_DES_ID}`) 
                                                ? 'Failed to load products for this route' 
                                                : 'No products available for this route')
                                            : 'Select route to load available products'
                                        }
                                    </Typography>
                                    {formData.TXT_ORI_ID && formData.TXT_DES_ID && errorCache.has(`${formData.TXT_ORI_ID}-${formData.TXT_DES_ID}`) && (
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={retryLoadProducts}
                                            sx={{ mt: 0.5 }}
                                        >
                                            Retry
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Product Info and Base Rate Information */}
                    <Grid item xs={12}>
                        {/* Only show rate calculation info for new bookings, not in edit mode */}
                        {!booking && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Rate Calculation Info:</strong> If no specific rate is configured for your weight range, the system will automatically use the product's base rate as fallback.
                                </Typography>
                            </Alert>
                        )}
                        
                        {baseRateInfo && (
                            <Alert 
                                severity="success"
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="body2">
                                    <strong>Rate Calculation Details:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Billing Customer:</strong> {baseRateInfo.billing_customer?.name || 'No customer selected'} ({baseRateInfo.billing_customer?.code || 'N/A'})
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Billing Mode:</strong> {baseRateInfo.billing_mode}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Weight Used:</strong> {baseRateInfo.weight_used} kg
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Zone:</strong> {baseRateInfo.zone_name || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Rate Type:</strong> {baseRateInfo.rate_type === 'default' ? 'Default Rate' : 'Extended Rate'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Product Type:</strong> {baseRateInfo.product_type}
                                </Typography>
                                {baseRateInfo.default_weight_range && (
                                    <Typography variant="body2">
                                        <strong>Default Weight Range:</strong> {baseRateInfo.default_weight_range.from}-{baseRateInfo.default_weight_range.to} kg
                                    </Typography>
                                )}
                                <Typography variant="body2">
                                    <strong>Base Rate:</strong> ₹{baseRateInfo.base_rate}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Additional Charge:</strong> {baseRateInfo.additional_charge_display}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Discounts Applied:</strong> Basic Freight {formatDiscountPercent(baseRateInfo.package_discount)}%, Fuel Surcharge {formatDiscountPercent(baseRateInfo.fuel_discount)}%
                                </Typography>
                            </Alert>
                        )}
                    </Grid>

                    {/* Rate Calculation Button */}
                    <Grid item xs={12} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                calculateRate();
                            }}
                            disabled={viewMode || calculating || loadingProducts || !formData.TXT_ORI_ID || !formData.TXT_DES_ID || !formData.product_id || !formData.charged_wt || !products.length}
                            startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
                            sx={{ height: 56 }}
                        >
                            {calculating ? 'Calculating...' : 'Calculate Rate'}
                        </Button>
                        {!products.length && formData.TXT_ORI_ID && formData.TXT_DES_ID && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                No products available for rate calculation
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Pricing Section */}
                <Typography variant="h6" gutterBottom>Pricing Details</Typography>
                <Grid container spacing={3}>
                    {/* Basic Charges */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Basic Freight (₹)"
                            type="number"
                            value={formData.basic_freight}
                            onChange={(e) => handleInputChange('basic_freight', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Fuel Surcharge (₹)"
                            type="number"
                            value={formData.fuel_surcharge}
                            onChange={(e) => handleInputChange('fuel_surcharge', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label={`Discounted Basic Freight ${baseRateInfo && (baseRateInfo.package_discount > 0) ? `(${formatDiscountPercent(baseRateInfo.package_discount)}%)` : '(0%)'}`}
                            type="number"
                            value={formData.discounted_basic_freight}
                            onChange={(e) => handleInputChange('discounted_basic_freight', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText={baseRateInfo?.discount_applied_for === 'BILL TO THIRD PARTY' ? 'No discount - Third party billing' : 
                                       baseRateInfo?.package_discount > 0 ? `Customer discount applied` : 'No customer discount'}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label={`Discounted Fuel Surcharge ${baseRateInfo && (baseRateInfo.fuel_discount > 0) ? `(${formatDiscountPercent(baseRateInfo.fuel_discount)}%)` : '(0%)'}`}
                            type="number"
                            value={formData.discounted_fuel_surcharge}
                            onChange={(e) => handleInputChange('discounted_fuel_surcharge', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText={baseRateInfo?.discount_applied_for === 'BILL TO THIRD PARTY' ? 'No discount - Third party billing' : 
                                       baseRateInfo?.fuel_discount > 0 ? `Customer discount applied` : 'No customer discount'}
                        />
                    </Grid>

                    {/* Additional Charges */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Handling Charges (₹)"
                            type="number"
                            value={formData.handling_charges}
                            onChange={(e) => handleInputChange('handling_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Statistical Charges (₹)"
                            type="number"
                            value={formData.stat_charges}
                            onChange={(e) => handleInputChange('stat_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="To Pay Surcharge (₹)"
                            type="number"
                            value={formData.to_pay_surcharge}
                            onChange={(e) => handleInputChange('to_pay_surcharge', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="C.O.D Coll.Charge Rs"
                            type="number"
                            value={formData.cod_charges}
                            onChange={(e) => handleInputChange('cod_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Special Service (₹)"
                            type="number"
                            value={formData.sps_charges}
                            onChange={(e) => handleInputChange('sps_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Value Surcharge (₹)"
                            type="number"
                            value={formData.value_surc}
                            onChange={(e) => handleInputChange('value_surc', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Carrier's Risk (₹)"
                            type="number"
                            value={formData.cr_charges}
                            onChange={(e) => handleInputChange('cr_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Remote Area Service Charges (₹)"
                            type="number"
                            value={formData.remote_area_charges}
                            onChange={(e) => handleInputChange('remote_area_charges', e.target.value)}
                            disabled={viewMode}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText="Additional charges for remote area delivery"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Total Calculation */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Total Amount
                                </Typography>
                                <Typography variant="h6">
                                    {bookingService.formatCurrency(formData.total_amt)}
                                </Typography>
                                {formData.product_tax_calculation_type === 'inclusive' && (
                                    <Typography variant="caption" color="text.secondary">
                                        Tax inclusive pricing
                                    </Typography>
                                )}
                                {formData.product_tax_calculation_type === 'exclusive' && (
                                    <Typography variant="caption" color="text.secondary">
                                        Tax exclusive pricing
                                    </Typography>
                                )}
                                {!formData.product_tax_calculation_type && (
                                    <Typography variant="caption" color="error">
                                        Tax type not determined
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    GST (18%)
                                </Typography>
                                <Typography variant="h6">
                                    {bookingService.formatCurrency(formData.ser_tax)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formData.product_tax_calculation_type === 'inclusive' ? 'Extracted from total' : 'Added to base'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card variant="outlined" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                            <CardContent>
                                <Typography variant="body2">
                                    Grand Total
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    {bookingService.formatCurrency(formData.grand_total)}
                                </Typography>
                                <Typography variant="caption">
                                    {formData.product_tax_calculation_type === 'inclusive' ? 'Including tax' : 'Base + tax'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
            </>
        );
    };

    if (loading) {
        return (
            <DashboardLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box sx={{ mb: 3 }}>
                {/* Breadcrumbs */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    {getBreadcrumbs()}
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {getPageTitle()}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {isCreateMode && 'Create a new courier booking with detailed information'}
                            {isEditMode && 'Modify booking details and information'}
                            {isViewMode && 'View complete booking information (read-only)'}
                        </Typography>
                    </Box>
                    
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/employee/bookings')}
                        >
                            Back to List
                        </Button>
                    </Stack>
                </Box>
            </Box>

            {/* Form Sections */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* General Details Section */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: formData?.awb_no && formData?.TXT_ORI_ID && formData?.TXT_DES_ID ? 'success.main' : 'grey.400',
                                        mr: 1
                                    }} 
                                />
                                General Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter the basic booking information including AWB number, route details, and transaction mode.
                            </Typography>
                        </Box>
                    </Box>
                    
                    {renderGeneralSection()}
                </Paper>

                {/* Shipper Details Section */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: formData?.frm_name && formData?.frm_address && formData?.frm_pincode ? 'success.main' : 'grey.400',
                                        mr: 1
                                    }} 
                                />
                                Shipper Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formData.payment_mode === 'BILL TO SHIPPER' 
                                    ? 'Enter the sender\'s information. You must select from existing customers.'
                                    : formData.payment_mode === 'BILL TO RECEIPIENT' || formData.payment_mode === 'BILL TO THIRD PARTY'
                                    ? 'Enter the sender\'s information. You can search from previous addresses or enter details manually.'
                                    : 'Enter the sender\'s information. You can search existing customers or enter details manually.'
                                }
                            </Typography>
                        </Box>
                    </Box>
                    
                    {renderCustomerSection('Shipper Details', 'frm_', false)}
                </Paper>

                {/* Recipient Details Section */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: formData?.to_name && formData?.to_address && formData?.to_pincode ? 'success.main' : 'grey.400',
                                        mr: 1
                                    }} 
                                />
                                Recipient Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formData.payment_mode === 'BILL TO RECEIPIENT' 
                                    ? 'Enter the recipient\'s information. You must select from existing customers.'
                                    : formData.payment_mode === 'BILL TO SHIPPER' || formData.payment_mode === 'BILL TO THIRD PARTY'
                                    ? 'Enter the recipient\'s information. You can search from previous addresses or enter details manually.'
                                    : 'Enter the recipient\'s information. You can search existing customers or enter details manually.'
                                }
                            </Typography>
                        </Box>
                    </Box>
                    
                    {renderCustomerSection('Recipient Details', 'to_', true)}
                </Paper>

                {/* Billing & Pricing Section */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: formData?.package_name && formData?.charged_wt && formData?.product_id ? 'success.main' : 'grey.400',
                                        mr: 1
                                    }} 
                                />
                                Billing & Pricing
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter package details, calculate rates, and review pricing information.
                            </Typography>
                        </Box>
                    </Box>
                    
                    {renderBillingSection()}
                    
                    {/* Submit Button for Billing Section */}
                    {!viewMode && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleFormSubmit}
                                startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                                size="large"
                                disabled={submitting || !formData?.package_name || !formData?.charged_wt || !formData?.product_id}
                            >
                                {submitting ? 'Saving...' : (isCreateMode ? 'Create' : 'Update') + ' Booking'}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Cancel Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleFormCancel}
                    startIcon={<CancelIcon />}
                >
                    {viewMode ? 'Close' : 'Cancel'}
                </Button>
            </Box>

        </DashboardLayout>
    );
};

export default BookingFormPage; 